import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Modal,
  TextContainer,
  TextField,
  Stack,
  ResourceList,
  Card,
  ResourceItem,
  ButtonGroup,
  Select,
  FormLayout,
  Banner,
  Checkbox,
  ProgressBar,
  Popover,
  ActionList,
  Heading,
  Subheading,
  TextStyle,
  EmptyState,
  Icon,
  List,
  Collapsible,
  Tooltip,
  Badge,
  Text,
  RadioButton,
  ChoiceList
} from '@shopify/polaris';
import { DeleteMajor, EditMajor, SearchMinor, DuplicateMinor, QuestionMarkMajor, QuestionMarkMinor } from '@shopify/polaris-icons';
import { useTemplateContext } from '../../contexts/TemplateContext';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import { useTemplate } from '../../contexts/TemplateContext';
import { PersistentResultsSidebar } from '../templates/PersistentResultsSidebar';
import { recordTemplateUsage } from '../../services/metricsService';
import { hasEnoughCredits } from '../../services/creditService';
import { TemplateSelector, TemplateHelp, WorkflowList, TemplateEditor, TemplateList, ImageTemplateExecutor } from '../templates';
import { getAvailableAIModels, getAIModelById } from '../../services/aiModelService';
import { processWithAIProvider } from '../../services/aiProviderService';
import { getOpenAIApiKey } from '../../services/apiKeyService';
import { getTemplateById } from '../../services/templateService';

export default function TemplateTools({ onSaveTemplate, onTemplateUpdate, onLoadTemplate, disabled, selectedProducts, handleProductChange, handleOptionValueChange, metafieldChangeHandler }) {
  // Template type options - now supporting both Product and Image Analyzing templates
  const templateTypes = [
    { label: 'Product', value: 'product' },
    { label: 'Image Analyzing', value: 'image' },
    { label: 'Tool Execution', value: 'tool' }
    // Variant type temporarily disabled
    // { label: 'Variant', value: 'variant' }
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showCreateWorkflowModal, setShowCreateWorkflowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [error, setError] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [workflowSearchValue, setWorkflowSearchValue] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [skuPrefix, setSkuPrefix] = useState('');
  const [skuSuffix, setSkuSuffix] = useState('');
  const [handlePrefix, setHandlePrefix] = useState('');
  const [handleSuffix, setHandleSuffix] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [workflowExecutionStatus, setWorkflowExecutionStatus] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState({
    id: '',
    name: '',
    type: 'product',
    prompt: '',
    target: 'description',
    metafield: {
      namespace: '',
      key: ''
    },
    userId: '',
    inputFields: [],
    outputFields: [],
    inputMetafields: [],
    outputMetafields: [],
    modelId: ''
  });
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
  const [showDeleteWorkflowModal, setShowDeleteWorkflowModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [updatedProducts, setUpdatedProducts] = useState({});
  const [availableAIModels, setAvailableAIModels] = useState([]);
  const [loadingAIModels, setLoadingAIModels] = useState(false);
  const [creditCost, setCreditCost] = useState(0);

  // State for AI Model Help modal
  const [showAIModelHelpModal, setShowAIModelHelpModal] = useState(false);

  const { user } = useAuth();
  const { currentShop } = useShop();
  const { 
    templates, 
    workflows,
    loading, 
    addTemplate, 
    updateTemplate, 
    deleteTemplate,
    addWorkflow,
    deleteWorkflow,
    updateWorkflow,
    executeTemplates,
    executeTemplatesDirectly,
    executeWorkflow,
    openaiSettings,
    getCurrentProductOptions,
    productEdits,
    editHistory,
    products
  } = useTemplateContext();

  useEffect(() => {
    const loadAIModels = async () => {
      setLoadingAIModels(true);
      try {
        const models = await getAvailableAIModels();
        setAvailableAIModels(models);
      } catch (err) {
        console.error('Error loading AI models:', err);
      } finally {
        setLoadingAIModels(false);
      }
    };

    loadAIModels();
  }, []);
  
  // Ensure a default selection for image templates
  useEffect(() => {
    if (editingTemplate.type === 'image') {
      // If no option is selected (neither body_html in outputFields nor sidebarOnly is true)
      if (!editingTemplate.outputFields?.includes('body_html') && !editingTemplate.sidebarOnly) {
        console.log('Setting default output mode for image template to Sidebar Only');
        setEditingTemplate(prev => ({
          ...prev,
          sidebarOnly: true,
          outputFields: []
        }));
      }
    }
  }, [editingTemplate.type, editingTemplate.outputFields, editingTemplate.sidebarOnly]);
  
  // Initialize prefix and suffix values when editing a template - only once when the template ID changes
  useEffect(() => {
    if (editingTemplate.id) {
      if (editingTemplate.prompt && editingTemplate.prompt.startsWith('tool:generate_skus')) {
        console.log('Raw prompt:', editingTemplate.prompt);
        const promptParams = editingTemplate.prompt.split('|').slice(1).join('|');
        console.log('Prompt params:', promptParams);
        
        const prefixMatch = promptParams.match(/prefix=([^|]*)/);
        const suffixMatch = promptParams.match(/suffix=([^|]*)/);
        
        console.log('Regex matches:', { prefixMatch, suffixMatch });
        
        const prefix = prefixMatch ? prefixMatch[1] : '';
        const suffix = suffixMatch ? suffixMatch[1] : '';
        
        console.log('Extracted values:', { prefix, suffix });
        
        setSkuPrefix(prefix);
        setSkuSuffix(suffix);
        
        // Make sure the template prompt includes both prefix and suffix
        if (suffix && !editingTemplate.prompt.includes('suffix=')) {
          console.log('Fixing template prompt to include suffix');
          setEditingTemplate(prev => ({
            ...prev,
            prompt: `tool:generate_skus|prefix=${prefix}|suffix=${suffix}`
          }));
        }
        
        console.log('Initialized SKU prefix and suffix once:', { prefix, suffix });
      }
      
      if (editingTemplate.prompt && editingTemplate.prompt.startsWith('tool:handle_prefix_suffix')) {
        console.log('Raw handle prompt:', editingTemplate.prompt);
        const promptParams = editingTemplate.prompt.split('|').slice(1).join('|');
        console.log('Handle prompt params:', promptParams);
        
        const prefixMatch = promptParams.match(/prefix=([^|]*)/);
        const suffixMatch = promptParams.match(/suffix=([^|]*)/);
        
        console.log('Handle regex matches:', { prefixMatch, suffixMatch });
        
        const prefix = prefixMatch ? prefixMatch[1] : '';
        const suffix = suffixMatch ? suffixMatch[1] : '';
        
        console.log('Extracted handle values:', { prefix, suffix });
        
        setHandlePrefix(prefix);
        setHandleSuffix(suffix);
        
        // Make sure the template prompt includes both prefix and suffix
        if (suffix && !editingTemplate.prompt.includes('suffix=')) {
          console.log('Fixing handle template prompt to include suffix');
          setEditingTemplate(prev => ({
            ...prev,
            prompt: `tool:handle_prefix_suffix|prefix=${prefix}|suffix=${suffix}`
          }));
        }
        
        console.log('Initialized handle prefix and suffix once:', { prefix, suffix });
      }
    }
  }, [editingTemplate.id]); // Only run when template ID changes

  // Add event listeners for external control
  useEffect(() => {
    const handleOpenTemplateManager = () => {
      setIsOpen(true);
    };
    
    const handleOpenWorkflowManager = () => {
      setShowWorkflowModal(true);
    };
    
    // Add event listeners if the component has an ID
    const element = document.getElementById('template-tools-ref');
    if (element) {
      element.addEventListener('open-template-manager', handleOpenTemplateManager);
      element.addEventListener('open-workflow-manager', handleOpenWorkflowManager);
      
      return () => {
        element.removeEventListener('open-template-manager', handleOpenTemplateManager);
        element.removeEventListener('open-workflow-manager', handleOpenWorkflowManager);
      };
    }
  }, []);

  const targetOptions = [
    { label: 'Product Description', value: 'description' },
    { label: 'Product Title', value: 'title' },
    { label: 'Metafield', value: 'metafield' }
  ];

  const examplePrompts = [
    { 
      label: 'Select an example prompt', 
      value: '' 
    },
    {
      label: 'Title Optimizer',
      value: 'Generate a compelling and SEO-optimized title for our product "{title}", ensuring it incorporates "{metafield.seo.keywords}" and is structured to rank effectively on Google and Amazon search platforms.\n\n' +
             'Instructions:\n\n' +
             'To use this prompt, the following placeholders should be replaced dynamically with the actual product information:\n\n' +
             '- {title} → The existing product title.\n' +
             '- {description} → A short summary of the product to understand its core features.\n' +
             '- {metafield.seo.keywords} → The relevant SEO keywords stored in the metafield that should be included for better ranking.\n\n' +
             'Example Prompt in Action:\n' +
             'Generate an SEO-friendly title for our "{title}" using the following information:\n\n' +
             '- Product Details: {description}\n' +
             '- Target Keywords: {metafield.seo.keywords}\n\n' +
             'The final title should be clear, attractive, and structured for maximum visibility on Google and Amazon.'
    },
    {
      label: 'Description Optimizer',
      value: 'Craft a compelling product summary for "{title}" that is engaging, SEO-optimized, and structured for Shopify compatibility. Incorporate relevant details from the existing description and enhance it with target keywords from SEO metafields.\n\n' +
             'We structure the description like this:\n\n' +
             '1. {title}\n' +
             '2. Overview of the product: {description}\n' +
             '3. Key Features\n' +
             '- {metafield.seo.keywords} (incorporate naturally within the content)\n' +
             '- Highlight distinctive attributes and benefits.\n' +
             '- Emphasize why it stands out from competitors.\n\n' +
             '4. Reason to choose this product: Explain why this product is a must-have, using persuasive language that drives conversions.\n\n' +
             '### Important Guidelines:\n' +
             '- Use bold text for important words with `<strong>text</strong>` in HTML.\n' +
             '- Structure the description using proper HTML headers (`<h2>`, `<h3>`, etc.) for Shopify compatibility.\n' +
             '- Do not use `*` or `**` for bold text. Always use HTML formatting (`<strong></strong>`).\n' +
             '- Do not include technical specifications—only descriptive and persuasive content.\n' +
             '- Convert sizes correctly for the U.S. market where applicable.\n\n' +
             'Ensure the final output is engaging, structured, and optimized for both user readability and search engine rankings.'
    },
    {
      label: 'Product Specifications Extractor',
      value: 'Extract only the relevant product specifications from the description of "{title}". Do not include any additional text, headlines, or marketing content.\n\n' +
             '### Specification Formatting Guidelines:\n' +
             '- Only include key product details such as material, dimensions, weight, care instructions, and package contents.\n' +
             '- Format each specification with a **bold label** followed by the information (e.g., `<strong>Material:</strong> 100% Cotton`).\n' +
             '- Use **US units first**, followed by metric units in parentheses where applicable (e.g., `<strong>Dimensions:</strong> 12 x 8 x 6 inches (30.5 x 20.3 x 15.2 cm)`).\n' +
             '- Separate each specification with a `<br>` tag to maintain readability.\n\n' +
             '### Unwanted Words or Sections to Prevent:\n' +
             'Do not include or reference any of the following in the specifications:\n' +
             '- Shipping (e.g., “Free shipping included”)\n' +
             '- Payment (e.g., “Payment details available on request”)\n' +
             '- Warranty (e.g., “Includes a 2-year warranty”)\n' +
             '- Dropshipping\n' +
             '- China, Hong Kong\n' +
             '- Free (e.g., “Free returns”)\n' +
             '- Customer service (e.g., “Contact our customer service for more details”)\n' +
             '- Return (e.g., “Eligible for returns within 30 days”)\n' +
             '- Contact (e.g., “Contact us for further details”)\n\n' +
             'Use the following product details as the source for extracting specifications:\n' +
             '- **Product Description:** {description}\n' +
             '- **SEO Keywords for Context:** {metafield.seo.keywords}\n\n' +
             'Return only the cleaned and formatted product specifications as structured HTML output.'
    },        
    {
      label: 'SEO Description Generator',
      value: 'Generate an SEO-optimized product description using the following information: {body_html}. The description should be compelling, highlight key features, and include relevant keywords.'
    },
    {
      label: 'Clothing Image Analyze',
      value: 'Analyze the clothing item shown in the image and create a detailed product description based on visible attributes only.\n\n### Key Attributes to Identify:\n- **Garment Type:** Identify the specific type (e.g., hoodie, dress, jacket, t-shirt)\n- **Material/Texture:** Describe visible fabric qualities (e.g., denim, knit, cotton, silk)\n- **Fit & Cut:** Note the silhouette and fit (e.g., slim fit, oversized, cropped, relaxed)\n- **Design Features:** List all visible elements (e.g., buttons, zippers, hood, pockets, collar type)\n- **Sleeve & Neck Style:** Specify the style (e.g., v-neck, crew neck, turtleneck, long sleeve)\n- **Pattern/Graphics:** Describe any visible designs (e.g., stripes, floral, minimalist print)\n- **Gender Target:** Only if visually clear (e.g., menswear, womenswear, unisex)\n\n### Selling Points:\nSuggest 2-3 key benefits or selling points based on the visible style and features (e.g., "perfect for layering," "versatile everyday piece," "eye-catching statement item").\n\n### Important Guidelines:\n- Focus ONLY on what is clearly visible in the image\n- Do NOT speculate about non-visible features\n- Do NOT mention specific brand names\n- Format output as structured content with clear sections\n- For image templates, respond with valid JSON format matching the requested output fields'
    }
  ];

  const filteredTemplates = useMemo(() => {
    return templates.filter(template =>
      template.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [templates, searchValue]);

  const filteredWorkflows = useMemo(() => {
    return workflows.filter(workflow =>
      workflow.name.toLowerCase().includes(workflowSearchValue.toLowerCase())
    );
  }, [workflows, workflowSearchValue]);

  const handleTemplateSelect = useCallback((templateId) => {
    setSelectedTemplates(prev => {
      const isSelected = prev.includes(templateId);
      return isSelected 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId];
    });
  }, []);

  const handleSaveWorkflow = async () => {
    if (!workflowName.trim() || selectedTemplates.length === 0) {
      setError('Please provide a workflow name and select at least one template');
      return;
    }

    try {
      const workflowData = {
        name: workflowName.trim(),
        templates: selectedTemplates
      };

      if (editingWorkflow) {
        await updateWorkflow(editingWorkflow.id, workflowData);
      } else {
        await addWorkflow(workflowData);
      }

      setWorkflowName('');
      setSelectedTemplates([]);
      setShowCreateWorkflowModal(false);
      setEditingWorkflow(null);
      setError('');
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError(err.message || 'Failed to save workflow');
    }
  };

  const handleCreateWorkflow = () => {
    setEditingWorkflow(null);
    setWorkflowName('');
    setSelectedTemplates([]);
    setShowCreateWorkflowModal(true);
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setWorkflowName(workflow.name);
    setSelectedTemplates(workflow.templates);
    setShowCreateWorkflowModal(true);
  };

  // Utility function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const processTemplateResults = useCallback(async (templateResults) => {
    if (!templateResults) return;
    
    console.log('=== Starting processTemplateResults ===');
    console.log('Template results:', JSON.stringify(templateResults, null, 2));
    
    // First, collect all updates for each product
    const productUpdates = {};
    
    Object.entries(templateResults || {}).forEach(([productId, productResult]) => {
      if (!productResult) return;
      console.log(`\n=== Processing product ${productId} ===`);
      
      // Get the product
      const product = selectedProducts.find(p => p.id && p.id.toString() === productId);
      if (!product) {
        console.log(`Product ${productId} not found in selected products`);
        
        // Try to find by index if productId is "unknown"
        if (productId === "unknown" && Object.keys(productResult).length > 0) {
          console.log('Trying to process "unknown" product ID as the first selected product');
          
          // Use the first product as a fallback
          if (selectedProducts.length > 0) {
            const firstProduct = selectedProducts[0];
            console.log(`Using first product ${firstProduct.id} for unknown product ID`);
            
            // Process all fields for this product
            Object.entries(productResult).forEach(([field, value]) => {
              if (field !== 'error' && value !== undefined && value !== null) {
                console.log(`Processing field ${field} with value:`, value);
                handleProductChange(firstProduct.id, field, value);
              }
            });
          }
        }
        return;
      }

      // Initialize updates collection for this product
      if (!productUpdates[productId]) {
        productUpdates[productId] = {
          metafields: []
        };
      }

      // Check if productResult is a nested object with templateIds as keys
      const isNestedResult = Object.values(productResult).some(value => 
        value && typeof value === 'object' && !Array.isArray(value)
      );

      // If nested structure, flatten the results first
      let flattenedResult = {};
      if (isNestedResult) {
        console.log('Detected nested template results structure, flattening...');
        // Combine all template results for this product
        Object.values(productResult).forEach(templateResult => {
          if (templateResult && typeof templateResult === 'object') {
            flattenedResult = { ...flattenedResult, ...templateResult };
          }
        });
        console.log('Flattened result:', flattenedResult);
      } else {
        flattenedResult = productResult;
      }

      // Process special 'title' field first to ensure it gets updated
      if (flattenedResult.title !== undefined && flattenedResult.title !== null) {
        console.log(`Processing title field with value: ${flattenedResult.title}`);
        productUpdates[productId].title = flattenedResult.title;
        
        // Immediately update the product state for UI
        handleProductChange(productId, 'title', flattenedResult.title);
      }

      // Process special 'alt_text' field to map it to image_alt_text
      if (flattenedResult.alt_text !== undefined && flattenedResult.alt_text !== null) {
        console.log(`Processing alt_text field with value: ${flattenedResult.alt_text}`);
        productUpdates[productId].image_alt_text = flattenedResult.alt_text;
        
        // Immediately update the product state for UI with the correct field name
        handleProductChange(productId, 'image_alt_text', flattenedResult.alt_text);
      }

      // Process special 'body_html' field to map it to descriptionHtml
      if (flattenedResult.body_html !== undefined && flattenedResult.body_html !== null) {
        console.log(`Processing body_html field with value: ${flattenedResult.body_html}`);
        productUpdates[productId].descriptionHtml = flattenedResult.body_html;
        
        // Immediately update the product state for UI with the correct field name
        handleProductChange(productId, 'descriptionHtml', flattenedResult.body_html);
      }

      // Process special 'option1_values' field to update option values
      if (flattenedResult.option1_values !== undefined && flattenedResult.option1_values !== null) {
        console.log(`Processing option1_values field with value:`, flattenedResult.option1_values);
        
        // Update the option1_values in productUpdates
        if (!productUpdates[productId].options) {
          productUpdates[productId].options = [];
        }
        
        // Ensure option1 exists
        if (!productUpdates[productId].options[0]) {
          productUpdates[productId].options[0] = {};
        }
        
        // Set the values
        productUpdates[productId].options[0].values = Array.isArray(flattenedResult.option1_values) ? 
          flattenedResult.option1_values : 
          [flattenedResult.option1_values];
        
        // Immediately update the product state for UI
        handleOptionValueChange(productId, 0, productUpdates[productId].options[0].values);
      }

      // Process special 'option2_values' field to update option values
      if (flattenedResult.option2_values !== undefined && flattenedResult.option2_values !== null) {
        console.log(`Processing option2_values field with value:`, flattenedResult.option2_values);
        
        // Update the option2_values in productUpdates
        if (!productUpdates[productId].options) {
          productUpdates[productId].options = [];
        }
        
        // Ensure option2 exists
        if (!productUpdates[productId].options[1]) {
          productUpdates[productId].options[1] = {};
        }
        
        // Update the values
        productUpdates[productId].options[1].values = Array.isArray(flattenedResult.option2_values) ? 
          flattenedResult.option2_values : 
          [flattenedResult.option2_values];
        
        // Immediately update the product state for UI
        handleOptionValueChange(productId, 1, productUpdates[productId].options[1].values);
      }

      // Process all fields from the flattened result
      Object.entries(flattenedResult).forEach(([field, value]) => {
        if (field === 'title' || field === 'error') return; // Skip title (already processed) and error
        
        if (field === 'metafields' && typeof value === 'object') {
          // Process metafields object
          console.log('Processing metafields object:', value);
          
          // Iterate through all keys in the metafields object
          Object.entries(value).forEach(([metafieldKey, metaValue]) => {
            if (metafieldKey.includes('.')) {
              // Handle dot notation keys like "custom.product_specifications"
              const [namespace, key] = metafieldKey.split('.');
              if (metaValue !== undefined && metaValue !== null) {
                console.log(`Processing metafield ${namespace}.${key} with value:`, metaValue);
                
                // Add to the metafields collection for batch update
                productUpdates[productId].metafields.push({
                  namespace,
                  key,
                  value: metaValue
                });
                
                // Also update UI immediately
                if (metafieldChangeHandler) {
                  // Use metafieldChangeHandler if available (directly updates the metafield)
                  metafieldChangeHandler(productId, `${namespace}.${key}`, metaValue);
                } else {
                  // Fallback to handleProductChange
                  handleProductChange(productId, `metafields.${namespace}.${key}`, metaValue);
                }
              }
            } else if (typeof metaValue === 'object') {
              // Handle nested structure: { namespace: { key: value } }
              const namespace = metafieldKey;
              Object.entries(metaValue).forEach(([key, nestedValue]) => {
                if (nestedValue !== undefined && nestedValue !== null) {
                  console.log(`Processing metafield ${namespace}.${key} with value:`, nestedValue);
                  
                  // Add to the metafields collection for batch update
                  productUpdates[productId].metafields.push({
                    namespace,
                    key,
                    value: nestedValue
                  });
                  
                  // Also update UI immediately
                  if (metafieldChangeHandler) {
                    // Use metafieldChangeHandler if available
                    metafieldChangeHandler(productId, `${namespace}.${key}`, nestedValue);
                  } else {
                    // Fallback to handleProductChange
                    handleProductChange(productId, `metafields.${namespace}.${key}`, nestedValue);
                  }
                }
              });
            }
          });
        } else if (field.includes('.') && !field.startsWith('variants')) {
          // Handle dot notation for metafields (e.g., "namespace.key")
          const [namespace, key] = field.split('.');
          if (value !== undefined && value !== null) {
            console.log(`Processing dot notation metafield ${namespace}.${key} with value:`, value);
            
            // Add to the metafields collection for batch update
            productUpdates[productId].metafields.push({
              namespace,
              key,
              value
            });
            
            // Also update UI immediately
            if (metafieldChangeHandler) {
              // Use metafieldChangeHandler if available
              metafieldChangeHandler(productId, `${namespace}.${key}`, value);
            } else {
              // Fallback to handleProductChange
              handleProductChange(productId, `metafields.${namespace}.${key}`, value);
            }
          }
        } else if (value !== undefined && value !== null) {
          // Process regular fields
          console.log(`Processing field ${field} with value:`, value);
          
          // Update the product field
          handleProductChange(productId, field, value);
        }
      });
    });
    
    // Apply all updates to products
    console.log('Final product updates:', productUpdates);
    
    // Update UI to reflect changes
    setUpdatedProducts(prev => {
      const updated = { ...prev };
      Object.keys(productUpdates).forEach(productId => {
        updated[productId] = true;
      });
      return updated;
    });
    
    console.log('=== Completed processTemplateResults ===');
  }, [selectedProducts, handleProductChange, metafieldChangeHandler]);

  const handleExecute = async () => {
    if (selectedTemplates.length === 0) {
      setError('Please select at least one template');
      return;
    }

    setIsExecuting(true);
    setError('');
    
    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);
      
      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }

      console.log('Starting template execution with templates:', selectedTemplates);
      const templateResults = await executeTemplates(selectedTemplates, selectedProducts, (progress) => {
        setProgress(progress);
      });

      console.log('Template execution completed:', templateResults);
      
      if (!templateResults || Object.keys(templateResults).length === 0) {
        throw new Error('No results returned from template execution');
      }

      await processTemplateResults(templateResults);

      setIsExecuting(false);
      setProgress(0);
      setIsOpen(false);

    } catch (err) {
      console.error('Error executing templates:', err);
      setError(err.message || 'Failed to execute templates');
      setIsExecuting(false);
      setProgress(0);
    }
  };

  const handleExecuteWorkflow = async () => {
    if (!selectedProducts || selectedProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    if (selectedTemplates.length === 0) {
      setError('Please select at least one template');
      return;
    }

    setIsExecuting(true);
    setError('');
    setWorkflowExecutionStatus(null);
    
    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);
      
      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }

      console.log('Executing templates directly:', selectedTemplates);
      
      // Execute templates directly without creating a workflow
      const result = await executeTemplatesDirectly(
        selectedTemplates,
        selectedProducts, // Pass the full product objects
        (status) => {
          // Update progress and status
          setProgress(status.progress || 0);
          setWorkflowExecutionStatus(status);
          
          // Process results if available
          if (status.results && Object.keys(status.results).length > 0) {
            processTemplateResults(status.results);
          }
          
          // If workflow is complete, reset the executing state
          if (status.status === 'completed') {
            setIsExecuting(false);
            setProgress(0);
          }
        }
      );
      
      // Check if the result indicates we need to show the image template executor
      if (result && result.requiresImageSelection) {
        console.log('Image template detected:', result.imageTemplate);
        console.log('Template position:', result.templatePosition);
        
        // Process previous template results if they exist (respecting workflow order)
        if (result.previousTemplatesResults && Object.keys(result.previousTemplatesResults).length > 0) {
          console.log('Processing results from templates before the image template:', {
            resultCount: Object.keys(result.previousTemplatesResults).length
          });
          processTemplateResults(result.previousTemplatesResults);
        }
        
        // Store remaining templates to process after the image template
        if (result.remainingTemplateIds && result.remainingTemplateIds.length > 0) {
          console.log('Storing remaining templates to process after image template:', {
            count: result.remainingTemplateIds.length,
            templateIds: result.remainingTemplateIds
          });
          setRemainingTemplateIds(result.remainingTemplateIds);
        }
        
        // Check if this is an image template with useFeaturedImage=true
        if (result.imageTemplate && Boolean(result.imageTemplate.useFeaturedImage)) {
          console.log('Auto-processing image template with useFeaturedImage=true:', result.imageTemplate.name);
          // Process the template directly without showing the modal - keep execution state active
          // Note: Don't set isExecuting to false here, let the process handle it
          processImageTemplateDirectly(result.imageTemplate, result.remainingTemplateIds);
          return;
        }
        
        // For regular image templates, show the image selection UI
        console.log('Showing image selection UI for template without useFeaturedImage');
        setCurrentImageTemplate(result.imageTemplate);
        setRemainingAfterImage(result.remainingTemplateIds || []);
        setShowImageTemplateExecutor(true);
        setIsExecuting(false); // Stop the executing state since we're showing the image UI
        return;
      }
      
      // If there was an error with image templates, show it
      if (result && result.imageTemplates && result.error) {
        setError(result.error);
        setIsExecuting(false);
        return;
      }
      
      // Normal execution flow continues
      const executionId = result;
      console.log('Template execution started with ID:', executionId);
    } catch (err) {
      console.error('Error executing workflow:', err);
      setError(err.message || 'Failed to execute workflow');
      setIsExecuting(false);
      setProgressSafe(0); // Use setProgressSafe instead of setProgress
      // Clear the status
      setWorkflowExecutionStatus(null);
    }
  };

  const handleDeleteClick = useCallback((item, type) => {
    setItemToDelete(item);
    if (type === 'template') {
      setShowDeleteTemplateModal(true);
    } else {
      setShowDeleteWorkflowModal(true);
    }
  }, []);

  const handleConfirmDelete = useCallback(async (type) => {
    try {
      if (type === 'template') {
        await deleteTemplate(itemToDelete.id);
        setShowDeleteTemplateModal(false);
      } else {
        await deleteWorkflow(itemToDelete.id);
        setShowDeleteWorkflowModal(false);
      }
      setItemToDelete(null);
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      setError(`Failed to delete ${type}`);
    }
  }, [deleteTemplate, deleteWorkflow, itemToDelete]);

  const handleDelete = useCallback(async (templateId) => {
    handleDeleteClick({ id: templateId }, 'template');
  }, [handleDeleteClick]);

  const handleEdit = useCallback((template) => {
    console.log('Editing template with data:', template);
    // Make sure we preserve all existing data when editing
    setEditingTemplate({
      id: template.id,
      name: template.name,
      type: template.type,
      prompt: template.prompt,
      target: template.target,
      metafield: template.metafield,
      userId: template.userId, // Preserve the userId
      inputFields: template.inputFields || [],
      outputFields: template.outputFields || [],
      inputMetafields: template.inputMetafields || [],
      outputMetafields: template.outputMetafields || [],
      modelId: template.modelId || '',
      // Add image template specific properties
      sidebarOnly: template.sidebarOnly,
      useFeaturedImage: template.useFeaturedImage,
      outputModes: template.outputModes || {},
      outputMetafieldModes: template.outputMetafieldModes || {}
    });
    setOriginalName(template.name);
    setShowSaveModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingTemplate.name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!editingTemplate.prompt.trim()) {
      setError('Template prompt is required');
      return;
    }

    try {
      // Get the selected model information
      const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
      
      // Calculate the credit cost
      const outputFieldCount = (editingTemplate.outputFields?.length || 0);
      const outputMetafieldsCount = (editingTemplate.outputMetafields?.length || 0);
      const totalOutputs = outputFieldCount + outputMetafieldsCount;
      const effectiveOutputCount = Math.max(totalOutputs, 1);
      const costPerOutput = selectedModel?.creditCostPerOutput || 1;
      
      console.log('Saving template with model info:', {
        modelId: selectedModel?.id || '',
        modelName: selectedModel?.displayName,
        costPerOutput,
        outputFields: outputFieldCount,
        outputMetafields: outputMetafieldsCount,
        totalOutputs,
        effectiveOutputCount,
        // Log image template specific properties
        useFeaturedImage: editingTemplate.useFeaturedImage,
        sidebarOnly: editingTemplate.sidebarOnly,
        outputModes: editingTemplate.outputModes,
        outputMetafieldModes: editingTemplate.outputMetafieldModes
      });
      
      // Create a clean template data object to ensure all properties are properly included
      const templateData = {
        // Basic template properties
        name: editingTemplate.name.trim(),
        prompt: editingTemplate.prompt.trim(),
        type: editingTemplate.type, // Preserve the template type selected in the UI
        target: editingTemplate.target,
        // Only include metafield if target is 'metafield' and both namespace and key exist
        metafield: editingTemplate.target === 'metafield' && 
                  editingTemplate.metafield?.namespace && 
                  editingTemplate.metafield?.key ? 
                  editingTemplate.metafield : null,
        // Ensure arrays are initialized
        inputFields: editingTemplate.inputFields || [],
        outputFields: editingTemplate.outputFields || [],
        inputMetafields: editingTemplate.inputMetafields || [],
        outputMetafields: editingTemplate.outputMetafields || [],
        // Save model information
        modelId: selectedModel?.id || editingTemplate.modelId || '',
        creditCostPerOutput: costPerOutput,
        // Image template specific properties - explicitly set Boolean values
        useFeaturedImage: editingTemplate.useFeaturedImage === true,
        sidebarOnly: editingTemplate.sidebarOnly === true,
        // Ensure objects are properly initialized
        outputModes: {...(editingTemplate.outputModes || {})},
        outputMetafieldModes: {...(editingTemplate.outputMetafieldModes || {})}
      };

      let savedTemplate;
      if (editingTemplate.id) {
        // Update existing template
        savedTemplate = await updateTemplate(editingTemplate.id, templateData);
      } else {
        // Create new template
        savedTemplate = await addTemplate(templateData);
      }

      // Call parent handler with the saved template
      if (savedTemplate) {
        onSaveTemplate(savedTemplate);
      }

      // Reset and close
      setEditingTemplate({
        id: '',
        name: '',
        type: 'product',
        prompt: '',
        target: 'description',
        metafield: { namespace: '', key: '' },
        userId: '',
        inputFields: [],
        outputFields: [],
        inputMetafields: [],
        outputMetafields: [],
        modelId: ''
      });
      setOriginalName('');
      setShowSaveModal(false);
      setError('');
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    }
  }, [editingTemplate, updateTemplate, addTemplate, onSaveTemplate, availableAIModels]);

  const resetForm = useCallback(() => {
    setEditingTemplate({
      id: '',
      name: '',
      type: 'product',
      prompt: '',
      target: 'description',
      metafield: {
        namespace: '',
        key: ''
      },
      userId: '',
      inputFields: [],
      outputFields: [],
      inputMetafields: [],
      outputMetafields: [],
      modelId: ''
    });
    setOriginalName('');
    setError('');
  }, []);

  const handleFieldChange = (fieldName, checked, type) => {
    setEditingTemplate(prev => ({
      ...prev,
      [type === 'input' ? 'inputFields' : 'outputFields']: checked 
        ? [...(prev[type === 'input' ? 'inputFields' : 'outputFields'] || []), fieldName]
        : (prev[type === 'input' ? 'inputFields' : 'outputFields'] || []).filter(f => f !== fieldName)
    }));
  };

  const handleMetafieldChange = (index, field, value, type) => {
    const newMetafields = [...(editingTemplate[type === 'input' ? 'inputMetafields' : 'outputMetafields'] || [])];
    newMetafields[index] = { ...newMetafields[index], [field]: value };
    setEditingTemplate(prev => ({ 
      ...prev, 
      [type === 'input' ? 'inputMetafields' : 'outputMetafields']: newMetafields 
    }));
  };

  const handleAddMetafield = (type) => {
    const newMetafields = [...(editingTemplate[type === 'input' ? 'inputMetafields' : 'outputMetafields'] || []), { namespace: '', key: '' }];
    setEditingTemplate(prev => ({ 
      ...prev, 
      [type === 'input' ? 'inputMetafields' : 'outputMetafields']: newMetafields 
    }));
  };

  const handleRemoveMetafield = (index, type) => {
    const newMetafields = [...(editingTemplate[type === 'input' ? 'inputMetafields' : 'outputMetafields'] || [])];
    newMetafields.splice(index, 1);
    setEditingTemplate(prev => ({ 
      ...prev, 
      [type === 'input' ? 'inputMetafields' : 'outputMetafields']: newMetafields 
    }));
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const duplicateTemplate = {
        ...template,
        id: '', // Clear ID so a new one will be generated
        name: `${template.name} (Copy)`,
      };
      delete duplicateTemplate.id; // Ensure ID is removed
      
      const savedTemplate = await addTemplate(duplicateTemplate);
      if (savedTemplate) {
        onSaveTemplate(savedTemplate);
      }
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError(err.message || 'Failed to duplicate template');
    }
  };

  const handleDuplicateWorkflow = async (workflow) => {
    try {
      const duplicateWorkflow = {
        name: `${workflow.name} (Copy)`,
        templates: [...workflow.templates]
      };
      
      await addWorkflow(duplicateWorkflow);
    } catch (err) {
      console.error('Error duplicating workflow:', err);
      setError(err.message || 'Failed to duplicate workflow');
    }
  };

  const getAvailableFields = (type) => {
    const productFields = [
      { label: 'Title', value: 'title' },
      { label: 'Description', value: 'body_html' },
      { label: 'Product Type', value: 'product_type' },
      { label: 'Tags', value: 'tags' },
      { label: 'Alt Text', value: 'alt_text' }
    ];

    const variantFields = [
      { label: 'SKU', value: 'variants.sku' },
      { label: 'Price', value: 'variants.price' },
      { label: 'Compare at Price', value: 'variants.compare_at_price' },
      { label: 'Inventory Quantity', value: 'variants.inventory_quantity' }
    ];

    // Check if this is a SKU/variant template
    const isSKUTemplate = editingTemplate.prompt?.toLowerCase().includes('sku');
    
    return isSKUTemplate ? [...productFields, ...variantFields] : productFields;
  };

  const handleExecuteWorkflowFromList = async (workflow) => {
    if (!workflow || !workflow.templates || workflow.templates.length === 0) {
      setError('Selected workflow has no templates');
      return;
    }

    if (!selectedProducts || selectedProducts.length === 0) {
      setError('Please select at least one product to apply the workflow');
      return;
    }

    // Set the selected templates to match the workflow
    setSelectedTemplates(workflow.templates);
    
    // Close the workflow modal
    setShowWorkflowModal(false);
    
    // Execute the workflow
    setIsExecuting(true);
    setError('');
    setWorkflowExecutionStatus(null);
    
    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);
      
      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }
      
      console.log('Starting workflow execution from list:', workflow.id, 'with templates:', workflow.templates);
      
      // Execute the workflow using the V2 service
      const executionId = await executeWorkflow(
        workflow, 
        selectedProducts.map(p => p.id.toString()),
        (status) => {
          // Update progress and status
          setProgress(status.progress || 0);
          setWorkflowExecutionStatus(status);
          
          // Process results if available
          if (status.results && Object.keys(status.results).length > 0) {
            processTemplateResults(status.results);
          }
          
          // If workflow is complete, reset the executing state
          if (status.status === 'completed') {
            setIsExecuting(false);
            setProgress(0);
          }
        }
      );
      
      console.log('Workflow execution started with ID:', executionId);
    } catch (err) {
      console.error('Error executing workflow from list:', err);
      setError(err.message || 'Failed to execute workflow');
      setIsExecuting(false);
      setProgressSafe(0);
      // Clear the status
      setWorkflowExecutionStatus(null);
    }
  };
  
  // State for image template execution
  // Using selectedProducts from props instead of local state
  const [showImageTemplateExecutor, setShowImageTemplateExecutor] = useState(false);
  const [currentImageTemplate, setCurrentImageTemplate] = useState(null);
  const [remainingTemplateIds, setRemainingTemplateIds] = useState([]);
  const [remainingAfterImage, setRemainingAfterImage] = useState([]);

  // State for persistent results sidebar
  const [persistentResults, setPersistentResults] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // Fix for progress display - ensure progress never exceeds 100%
  const setProgressSafe = (value) => {
    // Ensure progress is a number and capped at 100%
    const safeValue = typeof value === 'number' ? Math.min(value, 100) : 0;
    setProgress(safeValue);
  };
  
  // Helper function to format objects for human-readable description
  const formatObjectForDescription = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return String(obj);
    }
    
    // Handle arrays specially
    if (Array.isArray(obj)) {
      const items = obj.map(item => {
        if (typeof item === 'object' && item !== null) {
          return formatObjectForDescription(item);
        }
        return String(item);
      });
      return '<ul>' + items.map(item => `<li>${item}</li>`).join('') + '</ul>';
    }
    
    // Format object as a list of key-value pairs
    const parts = [];
    for (const [key, value] of Object.entries(obj)) {
      // Skip empty values
      if (value === null || value === undefined || value === '') {
        continue;
      }
      
      let formattedValue;
      if (typeof value === 'object') {
        formattedValue = formatObjectForDescription(value);
      } else {
        formattedValue = String(value);
      }
      
      // Format the key-value pair
      parts.push(`<strong>${key}:</strong> ${formattedValue}`);
    }
    
    return parts.join('<br>');
  };

  // Direct image template processing without showing any modal
  const processImageTemplateDirectly = async (template, remainingIds = []) => {
    try {
      // Template is passed directly to avoid redundant database calls
      if (!template) {
        throw new Error('Template not found');
      }
      
      console.log('DIRECT PROCESSING - Template details:', {
        id: template.id,
        name: template.name,
        type: template.type,
        useFeaturedImage: template.useFeaturedImage
      });
      
      // Create a notification to show that processing is happening
      setIsExecuting(true);
      setProgressSafe(0); // Use setProgressSafe instead of setProgress
      setWorkflowExecutionStatus({
        status: 'processing',
        message: `Processing image template '${template.name}' automatically in the background... 0%`
      });
      
      // Check if user has enough credits
      const hasCredits = await hasEnoughCredits(user?.uid, selectedProducts.length);
      if (!hasCredits) {
        throw new Error(`Not enough credits to process template for ${selectedProducts.length} products.`);
      }
      
      // Get the AI model for this template
      const model = await getAIModelById(template.modelId);
      if (!model) {
        throw new Error('AI model not found for this template');
      }
      
      // Debug logging to see model properties
      if (model) {
        console.log('=== MAX IMAGES DEBUG (TemplateTools) ===');
        console.log('Selected Model Debug:', {
          id: model.id,
          displayName: model.displayName,
          type: model.type,
          modelType: model.modelType,
          maxImages: model.maxImages,
          isVision: model.type === 'vision' || model.displayName?.toLowerCase().includes('vision'),
          allProperties: model
        });
        console.log('Raw maxImages value:', model.maxImages);
        console.log('Type of maxImages:', typeof model.maxImages);
        console.log('All model keys:', Object.keys(model));
        console.log('=== END MAX IMAGES DEBUG ===');
      }
      
      // Process each product
      const results = [];
      let processedCount = 0;
      
      for (let i = 0; i < selectedProducts.length; i++) {
        const product = selectedProducts[i];
        
        // Update progress - cap at 100%
        const progressPercent = Math.min(((i + 1) / selectedProducts.length) * 100, 100);
        setProgressSafe(progressPercent);
        console.log(`Auto-select image template progress: ${progressPercent}%`);
        setWorkflowExecutionStatus({
          status: 'processing',
          message: `Processing image template '${template.name}' automatically in the background... ${Math.round(progressPercent)}%`
        });
        
        // Find the featured image for this product
        const featuredImage = product.images?.find(img => img.position === 1) || product.images?.[0];
        if (!featuredImage) {
          console.warn(`No featured image found for product ${product.id}, skipping`);
          continue;
        }
        
        // Format the image for processing
        const formattedImage = {
          id: featuredImage.id,
          url: featuredImage.src || featuredImage.url,
          alt: featuredImage.alt || `Featured image for ${product.title || 'product'}`,
          position: featuredImage.position || 0
        };
        
        // Process the image with the AI provider - using direct API call like in ImageTemplateExecutor
        let basePrompt = template.prompt || 'We need all informations from the product image which is relevant for the product description.';
        
        // Replace any placeholders in the prompt with product data
        Object.entries(product).forEach(([key, value]) => {
          if (typeof value === 'string') {
            basePrompt = basePrompt.replace(new RegExp(`\{${key}\}`, 'g'), value);
          }
        });
        
        // Get all outputs (fields and metafields) for structured response
        const allOutputs = [...(template.outputFields || [])];
        
        // Add metafields to allOutputs, ensuring they're in string format
        if (template.outputMetafields && template.outputMetafields.length > 0) {
          template.outputMetafields.forEach(metafieldItem => {
            // Convert object format to string format if needed
            let metafieldId = '';
            
            if (typeof metafieldItem === 'string') {
              metafieldId = metafieldItem;
            } else if (typeof metafieldItem === 'object' && metafieldItem !== null) {
              if (metafieldItem.namespace && metafieldItem.key) {
                metafieldId = `metafield.${metafieldItem.namespace}.${metafieldItem.key}`;
                console.log(`Converted metafield object to string format: ${metafieldId}`);
              } else {
                console.log(`Invalid metafield object format:`, metafieldItem);
                return; // Skip this item
              }
            }
            
            if (metafieldId && !allOutputs.includes(metafieldId)) {
              allOutputs.push(metafieldId);
            }
          });
        }
        
        // Format the output fields for the prompt
        const formattedOutputs = allOutputs.map(field => {
          // For metafields, use a more readable format
          if (typeof field === 'string' && field.startsWith('metafield.')) {
            const parts = field.replace('metafield.', '').split('.');
            if (parts.length === 2) {
              return `${parts[0]}.${parts[1]}`;
            }
          }
          return field;
        });
        
        // Log the formatted outputs for debugging
        console.log('Formatted output fields for prompt:', formattedOutputs);
        
        // Add instructions for structured output
        // Use the actual field names in the example to avoid confusion
        const exampleField = formattedOutputs[0] || 'field_name';
        
        // Create a clear, explicit prompt with properly formatted field names
        let fieldsList = '';
        formattedOutputs.forEach(field => {
          fieldsList += `- "${field}"
`;
        });
        
        // Combine the user's original prompt with very explicit formatting instructions
        // This approach ensures we get a single string value for the metafield
        const processedPrompt = `${basePrompt}\n\nIMPORTANT FORMATTING INSTRUCTIONS:\nRespond with a JSON object that has EXACTLY this structure:\n{\n  \"${formattedOutputs[0]}\": \"A single plain text string with all the information combined\"\n}\n\nYour response MUST be a single text string inside the JSON value, NOT a nested object or array. All information should be combined into ONE CONTINUOUS TEXT STRING.\n\nExample of CORRECT format:\n{ \"${formattedOutputs[0]}\": \"White cropped cardigan with long bell sleeves and front tie closure, made of ribbed knit fabric. Casual style with slightly open front. Paired with high waist denim jeans with tie belt. Accessories include black shoulder bag and gold jewelry.\" }\n\nExample of INCORRECT format:\n{ \"${formattedOutputs[0]}\": { \"product_type\": \"Women's cropped cardigan\", \"color\": \"White\" } }`;
        
        console.log('Requesting structured JSON with fields:', formattedOutputs);
        
        // Make AI API call based on model provider
        let jsonResponse;
        
        if (model.provider === 'openai') {
          // OpenAI direct API call
          const apiKey = await getOpenAIApiKey(user?.uid);
          if (!apiKey) {
            throw new Error('OpenAI API key is required for image analysis');
          }
          
          // Get the model to use (default to gpt-4.1-mini if not specified)
          const modelInternalName = model?.internalName || 'gpt-4.1-mini';
          
          // Prepare the messages with system and user content
          const messages = [
            {
              role: 'system',
              content: 'You are an AI assistant that generates product content for an e-commerce platform based on images. Follow the user\'s prompt exactly to determine what information to extract from the image. Do not make assumptions about what information is needed unless specified in the prompt.'
            },
            {
              role: 'user',
              content: [
                {
                  type: 'image_url',
                  image_url: {
                    url: formattedImage.url,
                    detail: 'high'
                  }
                },
                {
                  type: 'text',
                  text: processedPrompt
                }
              ]
            }
          ];
          
          // Prepare the request payload
          const requestPayload = {
            model: modelInternalName,
            messages,
            temperature: 0.7,
            max_tokens: 2000
          };
          
          console.log('Making direct OpenAI API call with payload:', {
            model: requestPayload.model,
            messageCount: messages.length,
            imageUrl: formattedImage.url.substring(0, 50) + '...',
            prompt: processedPrompt.substring(0, 50) + (processedPrompt.length > 50 ? '...' : '')
          });
          
          // Make a direct API call to OpenAI
          const apiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify(requestPayload)
          });
          
          if (!apiResponse.ok) {
            const errorText = await apiResponse.text();
            console.error('OpenAI API error response:', errorText);
            throw new Error(`OpenAI API error: ${apiResponse.status}. ${errorText.substring(0, 100)}...`);
          }
          
          jsonResponse = await apiResponse.json();
          
        } else if (model.provider === 'gemini') {
          // Use centralized AI service for Gemini
          console.log('Using centralized AI service for Gemini vision');
          console.log('Selected model for debugging:', {
            id: model.id,
            modelId: model.modelId,
            internalName: model.internalName,
            provider: model.provider
          });
          
          // Prepare the messages for the AI service
          const messages = [
            {
              role: 'system',
              content: 'You are an AI assistant that generates product content for an e-commerce platform based on images. Follow the user\'s prompt exactly to determine what information to extract from the image. Do not make assumptions about what information is needed unless specified in the prompt.'
            },
            {
              role: 'user',
              content: processedPrompt
            }
          ];
          
          const requestData = {
            messages,
            images: [formattedImage.url],
            modelType: 'vision',
            provider: model.provider,
            templateType: 'image'
          };
          
          console.log('Making AI provider service call:', {
            modelId: model.id,
            provider: model.provider,
            messageCount: messages.length,
            imageCount: 1,
            imageUrls: [formattedImage.url.substring(0, 50) + '...'],
            prompt: processedPrompt.substring(0, 50) + (processedPrompt.length > 50 ? '...' : '')
          });
          
          // Use the centralized AI service
          const response = await processWithAIProvider({
            provider: model.provider,
            modelId: model.internalName || model.modelId,
            prompt: processedPrompt,
            messages: requestData.messages,
            images: requestData.images,
            modelType: requestData.modelType,
            templateType: requestData.templateType,
            userId: user?.uid
          });
          
          // Convert to OpenAI-compatible format
          jsonResponse = {
            choices: [{
              message: {
                content: response
              }
            }]
          };
          
        } else {
          throw new Error(`Unsupported model provider: ${model.provider}`);
        }
        
        // CRITICAL DEBUGGING: Log the complete API response
        console.log('COMPLETE RAW API RESPONSE JSON:', JSON.stringify(jsonResponse, null, 2));
        console.log('API response details:', {
          hasChoices: !!jsonResponse.choices,
          choicesLength: jsonResponse.choices?.length,
          firstChoice: jsonResponse.choices?.[0] ? 'Present' : 'Missing'
        });
        
        // Parse the response
        const rawContent = jsonResponse.choices[0].message.content;
        
        // CRITICAL DEBUGGING: Log the message content
        console.log('RAW MESSAGE CONTENT FROM API:', rawContent);
        
        // Try to parse the content as JSON if possible
        let parsedContent = rawContent;
        let isJsonResponse = false;
        
        // Clean up the response - remove markdown code blocks if present
        const cleanedContent = rawContent.replace(/```json|```/g, '').trim();
        console.log('Cleaned content:', cleanedContent);
        
        try {
          // Check if the content looks like JSON
          if (cleanedContent.trim().startsWith('{') && cleanedContent.trim().endsWith('}')) {
            parsedContent = JSON.parse(cleanedContent);
            isJsonResponse = true;
            console.log('Successfully parsed JSON response:', Object.keys(parsedContent));
          }
        } catch (error) {
          console.log('Content is not valid JSON, using as plain text:', error.message);
        }
        
        // Create a structured response object that maps to the requested fields
        let structuredContent = {};
        
        if (isJsonResponse) {
          // If we have a JSON response, use it directly
          structuredContent = parsedContent;
          
          // Check if the response contains [object Object] as a key
          if (structuredContent['[object Object]']) {
            console.log('Found [object Object] in response, attempting to fix');
            
            // Try to parse it if it's a string that looks like JSON
            const objValue = structuredContent['[object Object]'];
            if (typeof objValue === 'string' && objValue.includes('{') && objValue.includes('}')) {
              try {
                // Clean up any markdown formatting
                const cleanValue = objValue.replace(/```json|```/g, '').trim();
                const innerJson = JSON.parse(cleanValue);
                
                // Replace the [object Object] key with the proper field name
                delete structuredContent['[object Object]'];
                
                // Get the first field from the template
                const firstField = allOutputs[0];
                if (firstField) {
                  // If the inner JSON has the correct field, use that value
                  const formattedField = firstField.startsWith('metafield.') ? 
                    firstField.replace('metafield.', '').replace('.', '_') : firstField;
                    
                  if (innerJson[formattedField] || innerJson[firstField]) {
                    structuredContent[firstField] = innerJson[formattedField] || innerJson[firstField];
                  } else {
                    // Otherwise, use a stringified version of the inner JSON
                    structuredContent[firstField] = JSON.stringify(innerJson);
                  }
                }
              } catch (e) {
                console.log('Failed to parse inner JSON:', e.message);
                // Keep the original value but assign it to the correct field
                const firstField = allOutputs[0];
                if (firstField) {
                  structuredContent[firstField] = objValue;
                  delete structuredContent['[object Object]'];
                }
              }
            } else {
              // It's not JSON, just use the value with the correct field name
              const firstField = allOutputs[0];
              if (firstField) {
                structuredContent[firstField] = objValue;
                delete structuredContent['[object Object]'];
              }
            }
          }
        } else {
          // If we have a plain text response, create a structured object
          // with the raw content assigned to each requested output field
          allOutputs.forEach(field => {
            structuredContent[field] = cleanedContent;
          });
          
          console.log('Created structured content from plain text:', Object.keys(structuredContent));
        }
        
        // Create a response object that matches what processWithAIProvider would return
        const response = { 
          content: structuredContent
        };
        
        // Format the results
        const productResult = {
          id: product.id,
          title: product.title,
          imageUrl: formattedImage.url,
          content: response.content,
          template: template.name,
          timestamp: new Date().toISOString()
        };
        
        results.push(productResult);
        
        // CRITICAL DEBUGGING: Log the response object
        console.log('Response object for product:', {
          productId: product.id,
          contentType: typeof response.content,
          contentPreview: typeof response.content === 'string' ? 
            response.content.substring(0, 100) : 
            JSON.stringify(response.content).substring(0, 100)
        });
        
        // Update the product if needed
        // CRITICAL FIX: Check for both outputFields AND outputMetafields
        if ((template.outputFields && template.outputFields.length > 0) || 
            (template.outputMetafields && template.outputMetafields.length > 0)) {
          const updates = {
            // Include the insert modes in the updates object
            insertModes: {}
          };
          
          // First, check for existing product content to ensure 'add' mode works correctly
          let existingContent = {};
          
          // For body_html, we need to make a special effort to find existing content
          if (product) {
            console.log(`Checking existing content for product ${product.id}`);
            
            // Try multiple ways to find the product description
            for (const field of template.outputFields || []) {
              // Special handling for body_html field
              if (field === 'body_html') {
                // Try all possible places where description might be stored
                const possibleSources = [
                  product.body_html,
                  product.descriptionHtml,
                  product.description,
                  // Check if there's an HTML description in metafields
                  product.metafields?.find(m => m.key === 'description_html')?.value,
                  // Check for SEO description
                  product.metafields?.find(m => m.key === 'description' && m.namespace === 'seo')?.value
                ];
                
                // Use the first non-empty source
                const foundContent = possibleSources.find(source => source && source.trim() !== '');
                
                if (foundContent) {
                  existingContent[field] = foundContent;
                  console.log(`Found existing content for ${field} from alternate source: ${foundContent.substring(0, 50)}...`);
                } else {
                  console.log(`No existing content found for ${field} in any source`);
                }
              } 
              // Standard handling for other fields
              else if (product[field]) {
                existingContent[field] = product[field];
                console.log(`Found existing content for ${field}: ${product[field].substring(0, 50)}...`);
              } else {
                console.log(`No existing content found for ${field} in product data`);
              }
            }
          }
          
          // Process both standard fields and metafields from the template
          // Create a combined list of all output targets
          const allOutputs = [...(template.outputFields || [])];
          
          // Add metafields to the list if they exist - check BOTH metafields and outputMetafields properties
          // First check for metafields property
          if (template.metafields && template.metafields.length > 0) {
            console.log('Processing metafields from template.metafields:', template.metafields);
            template.metafields.forEach(metafield => {
              // Format metafield ID as expected by the system
              const metafieldId = `metafield.${metafield.namespace}.${metafield.key}`;
              if (!allOutputs.includes(metafieldId)) {
                allOutputs.push(metafieldId);
                console.log(`Added metafield to outputs: ${metafieldId}`);
              }
            });
          }
          
          // CRITICAL: Also check for outputMetafields property which is used in your template
          if (template.outputMetafields && template.outputMetafields.length > 0) {
            console.log('Processing from template.outputMetafields:', template.outputMetafields);
            
            // Output metafields may be objects or strings, we need to handle both formats
            template.outputMetafields.forEach(metafieldItem => {
              // Convert object format to string format if needed
              let metafieldId = metafieldItem;
              
              // If it's an object with namespace and key properties, convert to string format
              if (typeof metafieldItem === 'object' && metafieldItem !== null) {
                if (metafieldItem.namespace && metafieldItem.key) {
                  metafieldId = `metafield.${metafieldItem.namespace}.${metafieldItem.key}`;
                  console.log(`Converted metafield object to string format: ${metafieldId}`);
                } else {
                  console.log(`Invalid metafield object format:`, metafieldItem);
                  return; // Skip this item
                }
              }
              
              // Ensure the metafieldId is a string and in the correct format
              if (typeof metafieldId === 'string' && !allOutputs.includes(metafieldId)) {
                // Make sure it has the metafield. prefix
                if (!metafieldId.startsWith('metafield.')) {
                  metafieldId = `metafield.${metafieldId}`;
                }
                
                allOutputs.push(metafieldId);
                console.log(`Added outputMetafield to outputs: ${metafieldId}`);
              }
            });
          }
          
          console.log('Processing all outputs in TemplateTools:', allOutputs);
          
          // Process each output field
          for (const field of allOutputs) {
            if (field.startsWith('metafield.')) {
              // Get the metafield parts (namespace.key)
              const metafieldParts = field.replace('metafield.', '').split('.');
              if (metafieldParts.length === 2) {
                const [namespace, key] = metafieldParts;
                
                // Look for the value in response with different key formats
                const metafieldKey = `${namespace}.${key}`;
                
                // Simple approach: first check if content is a string or object
                let metafieldValue = '';
                
                if (typeof response.content === 'string') {
                  // If the entire response is a string, use it for all fields
                  console.log(`Response content is a string, using full response for ${field}`);
                  metafieldValue = response.content;
                } else {
                  // For objects, try both namespaced and direct formats
                  // This matches how text templates would handle it
                  metafieldValue = response.content[metafieldKey] || response.content[field] || '';
                  console.log(`Looking for metafield in response under keys: ${metafieldKey} or ${field}`);
                }
                
                // If not found, try additional formats or use the entire response
                if (!metafieldValue) {
                  // Try without the metafield. prefix
                  const shortKey = key;
                  metafieldValue = response.content[shortKey] || '';
                  console.log(`Trying simple key format: ${shortKey}`);
                  
                  // If still not found, use the entire response as a fallback
                  if (!metafieldValue) {
                    console.log(`No content found for metafield ${field}, using full response content as fallback`);
                    metafieldValue = typeof response.content === 'object' ? 
                      JSON.stringify(response.content) : response.content;
                    console.log(`Using fallback content for ${field}:`, {
                      type: typeof metafieldValue,
                      length: typeof metafieldValue === 'string' ? metafieldValue.length : 'N/A',
                      preview: typeof metafieldValue === 'string' ? metafieldValue.substring(0, 50) + '...' : metafieldValue
                    });
                  }
                }
                
                // Create a separate metafields object if it doesn't exist
                if (!updates.metafields) {
                  updates.metafields = {};
                }
                
                // Store the metafield with its full field name to preserve the insert mode
                updates.metafields[field] = metafieldValue;
                console.log(`Setting metafield ${field} in updates.metafields to: ${typeof metafieldValue === 'string' ? metafieldValue.substring(0, 50) : metafieldValue}...`);
              } else {
                console.log(`Invalid metafield format: ${field}`);
              }
            } else {
              // Standard fields are set directly from response content
              updates[field] = response.content;
            }
            
            // Set the insert mode for this field from the template configuration
            // Get the configured output mode or default to 'exchange'
            // For metafields, check both the regular outputModes and the specific outputMetafieldModes
            let configuredMode = 'exchange';
            
            if (field.startsWith('metafield.')) {
              // For metafields, first check if there's a specific metafield mode
              const metafieldKey = field.replace('metafield.', '');
              configuredMode = template.outputMetafieldModes?.[metafieldKey] || template.outputModes?.[field] || 'exchange';
              console.log(`Metafield ${field} mode lookup: outputMetafieldModes.${metafieldKey}=${template.outputMetafieldModes?.[metafieldKey]}, outputModes.${field}=${template.outputModes?.[field]}, final mode=${configuredMode}`);
            } else {
              // For standard fields, use the regular outputModes
              configuredMode = template.outputModes?.[field] || 'exchange';
            }
            
            // For body_html, we always want to use 'add' mode regardless of configuration
            if (field === 'body_html') {
              updates.insertModes[field] = 'add';
              console.log(`Setting 'add' mode for body_html field in TemplateTools`);
              
              // Format the content with paragraph tags if needed
              if (updates[field] && typeof updates[field] === 'string' && !updates[field].includes('<p>')) {
                updates[field] = `<p>${updates[field]}</p>`;
                console.log(`Formatted body_html content with paragraph tags`);
              } else if (updates[field] && typeof updates[field] !== 'string') {
                console.log(`Warning: body_html content is not a string: ${typeof updates[field]}`);
                // Convert to string properly if it's an object
                if (typeof updates[field] === 'object' && updates[field] !== null) {
                  console.log('Converting object to formatted string for body_html');
                  // Format the object for readability
                  if (updates[field].body_html) {
                    // If the object has a body_html property, use that directly
                    updates[field] = updates[field].body_html;
                  } else {
                    // Otherwise, format the entire object for readability
                    updates[field] = formatObjectForDescription(updates[field]);
                  }
                } else {
                  // For other types, use String conversion
                  updates[field] = String(updates[field]);
                }
                
                // Add paragraph tags if needed
                if (!updates[field].includes('<p>')) {
                  updates[field] = `<p>${updates[field]}</p>`;
                }
              }
            } else {
              // Use the configured mode for other fields
              updates.insertModes[field] = configuredMode;
              console.log(`Using ${configuredMode} mode for ${field} field in TemplateTools`);
            }
            
            // CRITICAL: Pass the existing content for ALL fields that use 'add' mode
            if (updates.insertModes[field] === 'add') {
              // Initialize _originalContent if it doesn't exist
              if (!updates._originalContent) {
                updates._originalContent = {};
              }
              
              // For standard fields, use the existingContent object
              if (existingContent && existingContent[field]) {
                updates._originalContent[field] = existingContent[field];
                console.log(`Passing existing content directly for ${field}: ${existingContent[field].substring(0, 50)}...`);
              } 
              // For metafields, we need to check the product's metafields collection
              else if (field.startsWith('metafield.')) {
                // Extract namespace and key from the field
                const metafieldParts = field.replace('metafield.', '').split('.');
                if (metafieldParts.length === 2) {
                  const [namespace, key] = metafieldParts;
                  
                  // Look for the metafield in the product's metafields
                  if (product.metafields) {
                    const metafield = product.metafields.find(m => 
                      m.namespace === namespace && m.key === key
                    );
                    
                    if (metafield) {
                      updates._originalContent[field] = metafield.value;
                      console.log(`Found existing metafield ${field} content: ${metafield.value ? metafield.value.toString().substring(0, 50) + '...' : 'null or undefined'}`);
                    }
                  }
                }
              }
            }
          }
          
          // Log what we're updating
          console.log(`Updating product ${product.id} with:`, {
            fields: Object.keys(updates).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
            metafields: updates.metafields ? Object.keys(updates.metafields) : 'none',
            insertModes: updates.insertModes,
            hasOriginalContent: !!updates._originalContent
          });
          
          if (handleProductChange) {
            // CRITICAL: Make sure metafields are properly included in the updates
            // This ensures they're processed correctly by the product field handlers
            handleProductChange(product.id, 'all', updates);
          }
        }
        
        // Update progress
        processedCount++;
        const progress = Math.min(Math.round((processedCount / selectedProducts.length) * 100), 100);
        setWorkflowExecutionStatus({
          status: 'processing',
          message: `Processing image template '${template.name}': ${progress}% complete...`
        });
      }
      
      // Record template usage once after all products are processed
      // This prevents concurrent Firestore updates that can cause errors
      if (selectedProducts.length > 0) {
        try {
          await recordTemplateUsage(user?.uid, currentShop?.id, template.id);
          console.log('Template usage recorded successfully');
        } catch (error) {
          console.warn('Error recording template usage:', error);
          // Non-critical error, don't throw
        }
      }
      
      // Add results to the persistent sidebar only if sidebarOnly is true
      if (results.length > 0 && Boolean(template.sidebarOnly)) {
        console.log('Template has sidebarOnly enabled, adding results to persistent sidebar');
        setPersistentResults(prev => {
          const existingResultsMap = {};
          prev.forEach(result => {
            if (result.id) {
              existingResultsMap[result.id] = true;
            }
          });
          
          const uniqueNewResults = results.filter(result => {
            return !result.id || !existingResultsMap[result.id];
          });
          
          return [...prev, ...uniqueNewResults];
        });
        
        // Show the sidebar only for sidebarOnly templates
        setSidebarVisible(true);
      } else if (results.length > 0) {
        console.log('Template does not have sidebarOnly enabled, skipping persistent sidebar');
      }
      
      // Only update workflow status as complete if there are no remaining templates
      if (!remainingIds || remainingIds.length === 0) {
        // This is the final template, so we can mark the workflow as complete
        setIsExecuting(false);
        setProgressSafe(100); // Set to exactly 100%
        setWorkflowExecutionStatus({
          status: 'completed',
          message: `Image template '${template.name}' processed successfully!`
        });
        
        // Clear the status after a short delay to prevent it from staying stuck
        setTimeout(() => {
          setWorkflowExecutionStatus(null);
          setProgressSafe(0); // Reset progress to 0
          setIsExecuting(false); // Ensure execution state is reset
          console.log('Progress, execution state, and workflow status reset - no remaining templates');
        }, 1500); // Shorter timeout for better UX
      } else {
        // There are remaining templates to process, so we'll update the status but keep executing
        setProgressSafe(50); // Set to 50% to indicate we're halfway through the workflow
        setWorkflowExecutionStatus({
          status: 'processing',
          message: `Image template '${template.name}' completed. Processing remaining templates...`
        });
        console.log('Image template completed, continuing with remaining templates');
      }
      
      // Ensure execution state is properly reset even if there are no more templates
      if (!remainingIds || remainingIds.length === 0) {
        console.log('No remaining templates to process, ensuring execution state is reset');
        // Double-check that execution state is reset after the timeout
        setTimeout(() => {
          if (isExecuting) {
            console.log('Execution state was still true, forcing reset');
            setIsExecuting(false);
            setProgressSafe(0);
            setWorkflowExecutionStatus(null);
          }
        }, 2000);
      }
      
      // Process any remaining templates in the workflow
      if (remainingIds && remainingIds.length > 0) {
        console.log('Processing remaining templates after image template:', {
          count: remainingIds.length,
          templateIds: remainingIds
        });
        
        // Wait a short delay to ensure UI updates properly
        setTimeout(async () => {
          try {
            // Execute the remaining templates
            // Set executing state back to true for the remaining templates
            setIsExecuting(true);
            
            const remainingResult = await executeTemplatesDirectly(
              remainingIds,
              selectedProducts, // Pass the full product objects
              (status) => {
                // Scale the progress from 50-100% for the remaining templates
                // This creates a continuous progress bar across both image and text templates
                const scaledProgress = status.progress ? 50 + (status.progress / 2) : 50;
                setProgressSafe(scaledProgress);
                
                // Update the status message to show we're processing remaining templates
                const updatedStatus = {
                  ...status,
                  message: status.message ? status.message.replace('Processing templates', 'Processing remaining templates') : status.message
                };
                setWorkflowExecutionStatus(updatedStatus);
                
                // Process results if available
                if (status.results && Object.keys(status.results).length > 0) {
                  processTemplateResults(status.results);
                }
                
                // If workflow is complete, reset the executing state
                if (status.status === 'completed') {
                  setIsExecuting(false);
                  setProgressSafe(100); // Ensure we end at exactly 100%
                  
                  // Clear the status after a delay
                  setTimeout(() => {
                    setWorkflowExecutionStatus(null);
                    setProgressSafe(0);
                  }, 1500);
                }
              }
            );
            
            console.log('Remaining templates processed:', {
              success: !remainingResult.error,
              result: remainingResult
            });
          } catch (remainingError) {
            console.error('Error processing remaining templates:', remainingError);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error processing image template directly:', error);
      setWorkflowExecutionStatus({
        status: 'error',
        message: `Error processing template: ${error.message}`
      });
    }
  };

  const handleExecuteSingleTemplate = async (templateId) => {
    if (!selectedProducts || selectedProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    try {
      // Get the template details directly from the database to ensure we have the latest data
      const template = await getTemplateById(templateId);
      if (!template) {
        setError('Template not found');
        return;
      }
      
      // Debug the template data
      console.log('EXECUTE TEMPLATE - Template data:', {
        id: template.id,
        name: template.name,
        type: template.type,
        useFeaturedImage: template.useFeaturedImage,
        'typeof useFeaturedImage': typeof template.useFeaturedImage,
        'Boolean(useFeaturedImage)': Boolean(template.useFeaturedImage)
      });
      
      // Check if this is an image template with useFeaturedImage
      if (template.type === 'image' && Boolean(template.useFeaturedImage)) {
        // Process directly without showing any modal
        console.log('EXECUTE TEMPLATE - Detected image template with useFeaturedImage=true, processing directly without modal');
        processImageTemplateDirectly(template);
        return;
      }
      
      // For image templates without useFeaturedImage, show the modal
      if (template.type === 'image') {
        console.log('EXECUTE TEMPLATE - Showing image template modal for template without useFeaturedImage');
        setCurrentImageTemplate(template);
        setShowImageTemplateExecutor(true);
        return;
      }
    } catch (error) {
      console.error('Error in handleExecuteSingleTemplate:', error);
      setError(`Error executing template: ${error.message}`);
      return;
    }

    // For regular product templates, continue with normal execution
    setIsExecuting(true);
    setError('');
    setWorkflowExecutionStatus(null);
    
    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);
      
      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }

      console.log('Executing single template directly:', templateId);
      
      // Execute the template directly with full product objects instead of just IDs
      const executionId = await executeTemplatesDirectly(
        [templateId],
        selectedProducts, // Pass the full product objects
        (status) => {
          // Update progress and status
          setProgress(status.progress || 0);
          setWorkflowExecutionStatus(status);
          
          // Process results if available
          if (status.results && Object.keys(status.results).length > 0) {
            processTemplateResults(status.results);
          }
          
          // If execution is complete, reset the executing state
          if (status.status === 'completed') {
            setIsExecuting(false);
            setProgress(0);
          }
        }
      );
      
      console.log('Template execution started with ID:', executionId);
    } catch (err) {
      console.error('Error executing single template:', err);
      setError(err.message || 'Failed to execute template');
      setIsExecuting(false);
      setProgressSafe(0); // Use setProgressSafe instead of setProgress
      setWorkflowExecutionStatus(null);
    }
  };

  return (
    <div>
      <Stack vertical>
        {error && (
          <Banner status="critical" onDismiss={() => setError('')}>
            <p>{error}</p>
          </Banner>
        )}

        <TemplateSelector
          initialSelectedTemplates={selectedTemplates}
          onTemplateSelect={(templates) => setSelectedTemplates(templates)}
          onExecute={handleExecuteWorkflow}
          disabled={disabled}
          selectedProducts={selectedProducts}
          isExecuting={isExecuting}
          progress={progress}
          onOpenTemplateManager={() => setIsOpen(true)}
          onOpenWorkflowManager={() => setShowWorkflowModal(true)}
          onOpenHelp={() => setShowHelpModal(true)}
        />

        {isExecuting && (
          <div className="template-tools-progress-container">
            {workflowExecutionStatus && (
              <div className="template-tools-progress">
                <div className="template-tools-progress-message">
                  {/* Fix percentage in status message if needed */}
                  {workflowExecutionStatus.message && workflowExecutionStatus.message.includes('%') 
                    ? workflowExecutionStatus.message.replace(/\d+%/g, match => {
                        const percentage = parseInt(match, 10);
                        return percentage > 100 ? '100%' : match;
                      })
                    : workflowExecutionStatus.message
                  }
                </div>
                {isExecuting && (
                  <ProgressBar progress={Math.min(progress, 100)} />
                )}
              </div>
            )}
          </div>
        )}
      </Stack>

      {/* Template Manager Modal */}
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Template Manager"
        primaryAction={{
          content: 'Create New Template',
          onAction: () => {
            resetForm();
            setShowSaveModal(true);
          }
        }}
        secondaryActions={[
          {
            content: 'Execute Selected',
            onAction: () => {
              setIsOpen(false);
              handleExecute();
            },
            disabled: selectedTemplates.length === 0 || !selectedProducts || selectedProducts.length === 0
          },
          {
            content: 'Help',
            onAction: () => setShowHelpModal(true)
          },
          {
            content: 'Done',
            onAction: () => setIsOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            {loading ? (
              <TextContainer>
                <p>Loading templates...</p>
              </TextContainer>
            ) : (
              <>
                <TextField
                  placeholder="Search templates..."
                  value={searchValue}
                  onChange={setSearchValue}
                  prefix={<SearchMinor />}
                  clearButton
                  onClearButtonClick={() => setSearchValue('')}
                />

                {filteredTemplates.length === 0 && (
                  <TextContainer>
                    <p>{searchValue ? `No templates found matching "${searchValue}"` : 'No templates saved yet. Click "Create New Template" to get started.'}</p>
                  </TextContainer>
                )}

                <TemplateList
                  templates={filteredTemplates}
                  selectedTemplates={selectedTemplates}
                  handleTemplateSelect={handleTemplateSelect}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  handleDuplicate={handleDuplicateTemplate}
                  handleExecute={handleExecuteSingleTemplate}
                  aiModels={availableAIModels}
                />
              </>
            )}
          </Stack>
        </Modal.Section>
      </Modal>

      {/* Workflow Manager Modal */}
      <Modal
        open={showWorkflowModal}
        onClose={() => {
          setShowWorkflowModal(false);
          setError('');
          setWorkflowSearchValue('');
        }}
        title="Workflow Manager"
        primaryAction={{
          content: 'Create New Workflow',
          onAction: handleCreateWorkflow
        }}
        secondaryActions={[
          {
            content: 'Help',
            onAction: () => setShowHelpModal(true)
          },
          {
            content: 'Done',
            onAction: () => setShowWorkflowModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            {error && (
              <Banner status="critical" onDismiss={() => setError('')}>
                <p>{error}</p>
              </Banner>
            )}

            <TextField
              label="Search workflows"
              value={workflowSearchValue}
              onChange={setWorkflowSearchValue}
              clearButton
              onClearButtonClick={() => setWorkflowSearchValue('')}
              prefix={<Icon source={SearchMinor} />}
              autoComplete="off"
            />

            <WorkflowList
              workflows={filteredWorkflows}
              selectedTemplates={selectedTemplates}
              handleEditWorkflow={handleEditWorkflow}
              handleDelete={handleDeleteClick}
              handleDuplicate={handleDuplicateWorkflow}
              executeWorkflow={handleExecuteWorkflowFromList}
              templates={templates}
              aiModels={availableAIModels}
              disabled={!selectedProducts || selectedProducts.length === 0}
            />
          </Stack>
        </Modal.Section>
      </Modal>

      {/* Create/Edit Workflow Modal */}
      <Modal
        open={showCreateWorkflowModal}
        onClose={() => {
          setShowCreateWorkflowModal(false);
          setError('');
          setWorkflowName('');
          setSelectedTemplates([]);
          setEditingWorkflow(null);
        }}
        title={editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
        primaryAction={{
          content: editingWorkflow ? 'Save Changes' : 'Create Workflow',
          onAction: handleSaveWorkflow
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowCreateWorkflowModal(false);
              setError('');
              setWorkflowName('');
              setSelectedTemplates([]);
              setEditingWorkflow(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            <TextField
              label="Workflow Name"
              value={workflowName}
              onChange={setWorkflowName}
              autoComplete="off"
              error={error && !workflowName ? 'Name is required' : ''}
            />

            <TemplateList
              templates={templates}
              selectedTemplates={selectedTemplates}
              handleTemplateSelect={handleTemplateSelect}
              handleExecute={handleExecuteSingleTemplate}
              aiModels={availableAIModels}
            />
          </Stack>
        </Modal.Section>
      </Modal>

      {/* Help Modal */}
      <TemplateHelp 
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Template Delete Confirmation Modal */}
      <Modal
        open={showDeleteTemplateModal}
        onClose={() => {
          setShowDeleteTemplateModal(false);
          setItemToDelete(null);
        }}
        title="Delete Template"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: () => handleConfirmDelete('template')
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDeleteTemplateModal(false);
              setItemToDelete(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Are you sure you want to delete the template "{itemToDelete?.name}"? This action cannot be undone.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Workflow Delete Confirmation Modal */}
      <Modal
        open={showDeleteWorkflowModal}
        onClose={() => {
          setShowDeleteWorkflowModal(false);
          setItemToDelete(null);
        }}
        title="Delete Workflow"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: () => handleConfirmDelete('workflow')
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDeleteWorkflowModal(false);
              setItemToDelete(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Are you sure you want to delete the workflow "{itemToDelete?.name}"? This action cannot be undone.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Save Template Modal */}
      <Modal
        open={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setError('');
          resetForm();
        }}
        title={editingTemplate.id ? 'Edit Template' : 'Create Template'}
        primaryAction={{
          content: editingTemplate.id ? 'Update Template' : 'Create Template',
          onAction: handleSave
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowSaveModal(false);
              setError('');
              resetForm();
            }
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextField
              label="Template Name"
              value={editingTemplate.name}
              onChange={(value) => setEditingTemplate(prev => ({ ...prev, name: value }))}
              autoComplete="off"
              error={error && !editingTemplate.name ? 'Name is required' : ''}
            />

            <Select
              label="Template Type"
              options={templateTypes}
              value={editingTemplate.type}
              onChange={(value) => {
                // When changing to image type, we need to reset the model selection
                // to a vision-capable model if the current one isn't compatible
                const newState = {...editingTemplate, type: value};
                
                // If switching to image type, ensure we have a vision model selected
                if (value === 'image') {
                  const currentModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                  const isCurrentModelVisionCapable = currentModel?.type === 'vision';
                  
                  // If current model isn't vision-capable, find the first vision model
                  if (!isCurrentModelVisionCapable) {
                    const firstVisionModel = availableAIModels.find(m => m.type === 'vision');
                    if (firstVisionModel) {
                      newState.modelId = firstVisionModel.id;
                      console.log('Automatically selected vision model:', firstVisionModel.displayName);
                    }
                  }
                }
                
                setEditingTemplate(newState);
              }}
              helpText="Select the type of template you want to create"
            />
            
            {/* AI Model Settings Section - Only show for non-tool templates */}
            {editingTemplate.type !== 'tool' && (
            <Card sectioned>
              <Stack vertical spacing="tight">
                <TextContainer>
                  <Heading>AI Model Settings</Heading>
                  <Stack spacing="tight" alignment="center">
                    <p>Select which AI model to use for this template.</p>
                    <Tooltip content="Learn more about AI models">
                      <Button plain icon={QuestionMarkMajor} onClick={() => setShowAIModelHelpModal(true)} />
                    </Tooltip>
                  </Stack>
                </TextContainer>
                
                {/* Filter AI models based on template type */}
                <Select
                  label="AI Model"
                  options={loadingAIModels 
                    ? [{ label: 'Loading models...', value: '' }] 
                    : availableAIModels.length > 0 
                      ? availableAIModels
                          .filter(model => {
                            // For image templates, only show vision-capable models
                            if (editingTemplate.type === 'image') {
                              return model.type === 'vision';
                            }
                            // For product templates, only show text models
                            return model.type === 'text' || !model.type; // Include models without type for backward compatibility
                          })
                          .map(model => ({
                            label: model.displayName + (model.type === 'vision' ? ' (Vision)' : ''),
                            value: model.id
                          }))
                      : [{ label: 'No models available', value: '' }]
                  }
                  value={editingTemplate.modelId || ''}
                  onChange={(value) => {
                    setEditingTemplate(prev => ({ ...prev, modelId: value }));
                    // Calculate credit cost
                    const outputFieldCount = (editingTemplate.outputFields?.length || 0);
                    const outputMetafieldsCount = (editingTemplate.outputMetafields?.length || 0);
                    const totalOutputs = outputFieldCount + outputMetafieldsCount;
                    const effectiveOutputCount = Math.max(totalOutputs, 1);
                    const selectedModel = availableAIModels.find(m => m.id === value);
                    const costPerOutput = selectedModel?.creditCostPerOutput || 1;
                    
                    console.log('Saving template with model info:', {
                      modelId: selectedModel?.id || '',
                      modelName: selectedModel?.displayName,
                      costPerOutput,
                      outputFields: outputFieldCount,
                      outputMetafields: outputMetafieldsCount,
                      totalOutputs,
                      effectiveOutputCount,
                      // Log image template specific properties
                      useFeaturedImage: editingTemplate.useFeaturedImage,
                      sidebarOnly: editingTemplate.sidebarOnly,
                      outputModes: editingTemplate.outputModes,
                      outputMetafieldModes: editingTemplate.outputMetafieldModes
                    });
                    
                    setCreditCost(effectiveOutputCount * costPerOutput);
                  }}
                  disabled={loadingAIModels || availableAIModels.length === 0}
                  helpText={loadingAIModels 
                    ? 'Loading models...' 
                    : availableAIModels.length === 0 
                      ? 'No AI models available. Please contact an administrator.' 
                      : editingTemplate.type === 'image'
                        ? 'Select a vision-capable AI model for image analysis'
                        : 'Select the AI model to use for this template'
                  }
                />
                
                {availableAIModels.length > 0 && (
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack.Item>
                      <TextContainer>
                        <Text variant="bodyMd">Output Fields: {(editingTemplate.outputFields?.length || 0)}</Text>
                      </TextContainer>
                    </Stack.Item>
                    <Stack.Item>
                      <TextContainer>
                        <Text variant="bodyMd">Output Metafields: {(editingTemplate.outputMetafields?.length || 0)}</Text>
                      </TextContainer>
                    </Stack.Item>
                    <Stack.Item>
                      <TextContainer>
                        <Text variant="bodyMd">Total Outputs: {(editingTemplate.outputFields?.length || 0) + (editingTemplate.outputMetafields?.length || 0)}</Text>
                      </TextContainer>
                    </Stack.Item>
                    <Stack.Item>
                      <TextContainer>
                        <Text variant="bodyMd">Credits Per Output: {availableAIModels.find(m => m.id === editingTemplate.modelId)?.creditCostPerOutput || 1}</Text>
                      </TextContainer>
                    </Stack.Item>
                    <Stack.Item>
                      {(() => {
                        const outputFieldCount = (editingTemplate.outputFields?.length || 0);
                        const outputMetafieldsCount = (editingTemplate.outputMetafields?.length || 0);
                        const totalOutputs = outputFieldCount + outputMetafieldsCount;
                        const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                        const costPerOutput = selectedModel?.creditCostPerOutput || 1;
                        const totalCost = Math.max(totalOutputs, 1) * costPerOutput;
                        
                        console.log('BulkEditProduct Template Editor Credit Cost:', {
                          outputFields: outputFieldCount,
                          outputMetafields: outputMetafieldsCount,
                          totalOutputs,
                          modelId: editingTemplate.modelId,
                          modelName: selectedModel?.displayName,
                          costPerOutput,
                          totalCost
                        });
                        
                        return (
                          <Badge status="info">Total Credit Cost: {totalCost}</Badge>
                        );
                      })()}
                    </Stack.Item>
                  </Stack>
                )}
                
                {/* Display max images for vision models */}
                {(() => {
                  const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                  
                  // Debug logging to see model properties
                  if (selectedModel) {
                    console.log('=== MAX IMAGES DEBUG (TemplateTools) ===');
                    console.log('Selected Model Debug:', {
                      id: selectedModel.id,
                      displayName: selectedModel.displayName,
                      type: selectedModel.type,
                      modelType: selectedModel.modelType,
                      maxImages: selectedModel.maxImages,
                      isVision: selectedModel.type === 'vision' || selectedModel.displayName?.toLowerCase().includes('vision'),
                      allProperties: selectedModel
                    });
                    console.log('Raw maxImages value:', selectedModel.maxImages);
                    console.log('Type of maxImages:', typeof selectedModel.maxImages);
                    console.log('All model keys:', Object.keys(selectedModel));
                    console.log('=== END MAX IMAGES DEBUG ===');
                  }
                  
                  // Show for vision models
                  const isVisionModel = selectedModel && (
                    selectedModel.type === 'vision' || 
                    selectedModel.modelType === 'vision' ||
                    selectedModel.displayName?.toLowerCase().includes('vision') || 
                    selectedModel.maxImages ||
                    selectedModel.id?.toLowerCase().includes('vision')
                  );
                  
                  return isVisionModel ? (
                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f6f6f7', borderRadius: '6px' }}>
                      <Stack spacing="tight" alignment="center">
                        <Text variant="bodyMd" fontWeight="medium">Vision Model Capability:</Text>
                        <Badge status="info">Max Images: {selectedModel.maxImages || 'Not specified'}</Badge>
                        <Text variant="bodySm" color="subdued">
                          This vision model can analyze up to {selectedModel.maxImages || 1} image{(selectedModel.maxImages || 1) > 1 ? 's' : ''} per product
                        </Text>
                      </Stack>
                    </div>
                  ) : null;
                })()}
              </Stack>
            </Card>
            )}

            {/* Image Selection UI - Only show for Image Analyzing templates */}
            {editingTemplate.type === 'image' && (
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Heading>Image Settings</Heading>
                  <TextContainer>
                    <p>For Image Analyzing templates, you'll select images during execution.</p>
                    <p>The selected vision model will analyze the images and generate content based on what it sees.</p>
                  </TextContainer>
                  
                  <Banner status="info">
                    <p>During template execution, you'll have the option to use featured images automatically or select images manually.</p>
                  </Banner>
                  
                  <Stack vertical spacing="loose">
                    <Checkbox
                      label="Add Featured Image option during execution"
                      checked={editingTemplate.useFeaturedImage === true}
                      onChange={(checked) => {
                        console.log('Setting useFeaturedImage to:', checked);
                        setEditingTemplate(prev => ({...prev, useFeaturedImage: checked === true}));
                      }}
                      helpText="When enabled, users can choose to automatically use the featured image for each product.

Note: Without this option, image templates will always run first in workflows (regardless of order) since they require manual selection."
                    />
                  </Stack>

                </Stack>
              </Card>
            )}
            
            {/* Example Prompts and Prompt field - Only show for non-tool templates */}
            {editingTemplate.type !== 'tool' && (
            <Card>
              <Card.Section>
                <Stack vertical spacing="tight">
                  <Select
                    label="Example Prompts"
                    options={examplePrompts}
                    onChange={(value) => {
                      if (value) {
                        const isSKUTemplate = value.toLowerCase().includes('sku');
                        setEditingTemplate(prev => ({ 
                          ...prev, 
                          prompt: value,
                          // For SKU templates, ensure we have all variant fields
                          outputFields: isSKUTemplate 
                            ? [...new Set([
                                ...(prev.outputFields || []),
                                'variants',
                                'variants.sku',
                                'variants.price',
                                'variants.compare_at_price',
                                'variants.inventory_quantity'
                              ])]
                            : prev.outputFields
                        }));
                      }
                    }}
                    value={examplePrompts.find(p => p.value === editingTemplate.prompt)?.value || ''}
                  />
                  <TextField
                    label="Prompt"
                    value={editingTemplate.prompt}
                    onChange={(value) => setEditingTemplate(prev => ({ ...prev, prompt: value }))}
                    multiline={4}
                    autoComplete="off"
                    error={error && !editingTemplate.prompt ? 'Prompt is required' : ''}
                    helpText={
                      <span>
                        Use placeholders like {'{'}field_name{'}'} or {'{'}metafield.namespace.key{'}'} to reference input fields.
                        Response should be a JSON object with the selected output fields.
                      </span>
                    }
                  />
                </Stack>
              </Card.Section>
            </Card>
            )}

            {/* Fields Section - Show different heading for tool templates */}
            <Card sectioned>
              <TextContainer>
                {editingTemplate.type !== 'tool' ? (
                  <>
                    <Heading>Fields</Heading>
                    <p>Select the fields you want to use in your template.</p>
                  </>
                ) : (
                  <>
                    <Heading>Tool Execution</Heading>
                    <p>Select a tool to automate product updates without using AI credits.</p>
                  </>
                )}
              </TextContainer>

              <FormLayout>
                {/* Input Section - Only show for regular product templates */}
                {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                  <Card sectioned>
                    <TextContainer>
                      <Heading>Input Fields</Heading>
                    </TextContainer>
                  
                  <FormLayout>
                    {/* Product Fields Card */}
                    <Card sectioned>
                      <TextContainer>
                        <Subheading>Product Fields</Subheading>
                      </TextContainer>
                      <FormLayout>
                        <Checkbox
                          label="Title"
                          checked={editingTemplate.inputFields?.includes('title')}
                          onChange={(checked) => handleFieldChange('title', checked, 'input')}
                        />
                        <Checkbox
                          label="Description"
                          checked={editingTemplate.inputFields?.includes('body_html')}
                          onChange={(checked) => handleFieldChange('body_html', checked, 'input')}
                        />
                        <Checkbox
                          label="Product Type"
                          checked={editingTemplate.inputFields?.includes('product_type')}
                          onChange={(checked) => handleFieldChange('product_type', checked, 'input')}
                        />
                        <Checkbox
                          label="Tags"
                          checked={editingTemplate.inputFields?.includes('tags')}
                          onChange={(checked) => handleFieldChange('tags', checked, 'input')}
                        />
                        <Checkbox
                          label="Handle"
                          checked={editingTemplate.inputFields?.includes('handle')}
                          onChange={(checked) => handleFieldChange('handle', checked, 'input')}
                        />
                      </FormLayout>
                    </Card>

                    {/* SEO Fields */}
                    <Card sectioned>
                      <TextContainer>
                        <Subheading>SEO Fields</Subheading>
                      </TextContainer>
                      <FormLayout>
                        {/* SEO Title */}
                        {/* <Checkbox
                          label="SEO Title"
                          checked={editingTemplate.inputFields?.includes('seo_title')}
                          onChange={(checked) => handleFieldChange('seo_title', checked, 'input')}
                        /> */}
                        {/* SEO Description */}
                        {/* <Checkbox
                          label="SEO Description"
                          checked={editingTemplate.inputFields?.includes('seo_description')}
                          onChange={(checked) => handleFieldChange('seo_description', checked, 'input')}
                        /> */}
                      </FormLayout>
                    </Card>

                    {/* Media Fields */}
                    <Card sectioned>
                      <TextContainer>
                        <Subheading>Media Fields</Subheading>
                      </TextContainer>
                      <FormLayout>
                        <Checkbox
                          label="Product Images Alt Text"
                          checked={editingTemplate.inputFields?.includes('alt_text')}
                          onChange={(checked) => handleFieldChange('alt_text', checked, 'input')}
                        />
                      </FormLayout>
                    </Card>

                    {/* Options Fields */}
                    <Card sectioned>
                      <TextContainer>
                        <Subheading>Options</Subheading>
                      </TextContainer>
                      <FormLayout>
                        <Checkbox
                          label="Option1 Name"
                          checked={editingTemplate.inputFields?.includes('option1_name')}
                          onChange={(checked) => handleFieldChange('option1_name', checked, 'input')}
                        />
                        <Checkbox
                          label="Option1 Values"
                          checked={editingTemplate.inputFields?.includes('option1_values')}
                          onChange={(checked) => handleFieldChange('option1_values', checked, 'input')}
                        />
                        <Checkbox
                          label="Option2 Name"
                          checked={editingTemplate.inputFields?.includes('option2_name')}
                          onChange={(checked) => handleFieldChange('option2_name', checked, 'input')}
                        />
                        <Checkbox
                          label="Option2 Values"
                          checked={editingTemplate.inputFields?.includes('option2_values')}
                          onChange={(checked) => handleFieldChange('option2_values', checked, 'input')}
                        />
                        <Checkbox
                          label="Option3 Name"
                          checked={editingTemplate.inputFields?.includes('option3_name')}
                          onChange={(checked) => handleFieldChange('option3_name', checked, 'input')}
                        />
                        <Checkbox
                          label="Option3 Values"
                          checked={editingTemplate.inputFields?.includes('option3_values')}
                          onChange={(checked) => handleFieldChange('option3_values', checked, 'input')}
                        />
                      </FormLayout>
                    </Card>

                    {/* Variant Fields */}
                    {editingTemplate.type === 'variant' && (
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Variant Fields</Subheading>
                        </TextContainer>
                        <FormLayout>
                          <Checkbox
                            label="SKU"
                            checked={editingTemplate.inputFields?.includes('sku')}
                            onChange={(checked) => handleFieldChange('sku', checked, 'input')}
                          />
                          <Checkbox
                            label="Price"
                            checked={editingTemplate.inputFields?.includes('price')}
                            onChange={(checked) => handleFieldChange('price', checked, 'input')}
                          />
                          <Checkbox
                            label="Compare at Price"
                            checked={editingTemplate.inputFields?.includes('compare_at_price')}
                            onChange={(checked) => handleFieldChange('compare_at_price', checked, 'input')}
                          />
                          <Checkbox
                            label="Inventory"
                            checked={editingTemplate.inputFields?.includes('inventory')}
                            onChange={(checked) => handleFieldChange('inventory', checked, 'input')}
                          />
                        </FormLayout>
                      </Card>
                    )}
                    
                    {/* Input Metafields */}
                    <Card sectioned>
                      <TextContainer>
                        <Subheading>Input Metafields</Subheading>
                        <p>Select metafields to use as input for the template</p>
                      </TextContainer>
                      <FormLayout>
                        {(editingTemplate.inputMetafields || []).map((metafield, index) => (
                          <FormLayout.Group key={index} condensed>
                            <TextField
                              label="Namespace"
                              value={metafield.namespace}
                              onChange={(value) => handleMetafieldChange(index, 'namespace', value, 'input')}
                              autoComplete="off"
                            />
                            <TextField
                              label="Key"
                              value={metafield.key}
                              onChange={(value) => handleMetafieldChange(index, 'key', value, 'input')}
                              autoComplete="off"
                            />
                            <Button
                              plain
                              destructive
                              onClick={() => handleRemoveMetafield(index, 'input')}
                            >
                              Remove
                            </Button>
                          </FormLayout.Group>
                        ))}
                        <Button onClick={() => handleAddMetafield('input')}>
                          Add Input Metafield
                        </Button>
                      </FormLayout>
                    </Card>
                  </FormLayout>
                </Card>
                )}

                {/* Output Section */}
                <Card sectioned>
                  <TextContainer>
                    {editingTemplate.type === 'tool' ? (
                      <>
                        <Heading>Available Tools</Heading>
                        <p>Choose from the tools below.</p>
                      </>
                    ) : (
                      <>
                        <Heading>Output Fields</Heading>
                        <p>Select where the AI-generated content should be applied. {editingTemplate.type === 'image' ? 'The vision model will analyze the selected images and generate content for these fields.' : 'The response should be a JSON object with these fields.'}</p>
                      </>
                    )}
                  </TextContainer>
                                    <FormLayout>
                    {/* Image templates now use the radio button interface below instead of these sections */}
                    
                    {/* Product Fields - Only show for regular product templates */}
                    {editingTemplate.type === 'product' ? (
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Product Fields</Subheading>
                        </TextContainer>
                        <FormLayout>
                          <Checkbox
                            label="Title"
                            checked={editingTemplate.outputFields?.includes('title')}
                            onChange={(checked) => handleFieldChange('title', checked, 'output')}
                          />
                          <Checkbox
                            label="Description"
                            checked={editingTemplate.outputFields?.includes('body_html')}
                            onChange={(checked) => handleFieldChange('body_html', checked, 'output')}
                          />
                          <Checkbox
                            label="Product Type"
                            checked={editingTemplate.outputFields?.includes('product_type')}
                            onChange={(checked) => handleFieldChange('product_type', checked, 'output')}
                          />
                          <Checkbox
                            label="Tags"
                            checked={editingTemplate.outputFields?.includes('tags')}
                            onChange={(checked) => handleFieldChange('tags', checked, 'output')}
                          />
                          <Checkbox
                            label="Handle"
                            checked={editingTemplate.outputFields?.includes('handle')}
                            onChange={(checked) => handleFieldChange('handle', checked, 'output')}
                          />
                        </FormLayout>
                      </Card>
                    ) : editingTemplate.type === 'image' ? (
                      /* For image templates, show a simplified radio button interface */
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Image Template Output Options</Subheading>
                          <Banner status="warning">
                            <p><strong>Image Template Limitation:</strong> Image templates can only output to the Description field or display results in the sidebar.</p>
                          </Banner>
                        </TextContainer>

                        
                        <FormLayout>
                          <Stack vertical spacing="loose">
                            <RadioButton
                              label="Add to Product Description"
                              helpText="AI-generated content will be added to the end of your product description."
                              checked={!editingTemplate.sidebarOnly && editingTemplate.outputFields?.includes('body_html')}
                              id="image-output-description"
                              name="image-output-mode"
                              onChange={() => {
                                console.log('Selecting Description output mode');
                                // Enable Description output, disable sidebar-only
                                setEditingTemplate(prev => ({
                                  ...prev,
                                  sidebarOnly: false,
                                  outputFields: ['body_html'],
                                  outputModes: {
                                    ...(prev.outputModes || {}),
                                    'body_html': 'add'
                                  }
                                }));
                              }}
                            />
                            <RadioButton
                              label="Sidebar Only (No Changes to Product)"
                              helpText="AI-generated content will only be displayed in the sidebar and won't modify your product."
                              id="image-output-sidebar"
                              name="image-output-mode"
                              checked={editingTemplate.sidebarOnly === true}
                              onChange={() => {
                                console.log('Selecting Sidebar-only mode');
                                // Enable sidebar-only mode, remove all output fields
                                setEditingTemplate(prev => ({
                                  ...prev,
                                  sidebarOnly: true,
                                  outputFields: []
                                }));
                              }}
                            />
                          </Stack>
                        </FormLayout>
                        
                        <div style={{ marginTop: '16px' }}>
                          <Banner status="info">
                            <p>When using Image Analyzing templates, all other output fields are automatically disabled.</p>
                          </Banner>
                        </div>
                      </Card>
                    ) : (
                      /* For tool templates, show a specialized tool configuration interface */
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Tool Configuration</Subheading>
                          <Banner status="warning">
                            <p><strong>Tool Execution Template:</strong> Tool templates automate product tools without using AI credits.</p>
                          </Banner>
                        </TextContainer>

                        <FormLayout>
                          <Select
                            label="Select Tool"
                            options={[
                              { label: 'Select a tool...', value: '' },
                              { label: '--- Product Tools ---', value: 'header_product', disabled: true },
                              { label: 'Generate Image Alt Text', value: 'image_alt_text' },
                              { label: 'Set Vendor Name', value: 'vendor_name' },
                              { label: 'Generate SKUs', value: 'generate_skus' },
                              { label: 'Generate Handles from Title', value: 'generate_handles' },
                              { label: 'Add Prefix/Suffix to Handles', value: 'handle_prefix_suffix' },
                              { label: '--- Pricing Tools ---', value: 'header_pricing', disabled: true },
                              { label: 'Update Prices', value: 'update_prices' },
                              { label: 'Set Sales Price Factor', value: 'sales_price_factor' },
                              { label: 'Round Prices', value: 'round_prices' },
                              { label: 'Set Compare-at Price Discount', value: 'compare_price_discount' },
                              { label: 'Clear Compare Prices', value: 'clear_compare_prices' },
                              { label: '--- Inventory Tools ---', value: 'header_inventory', disabled: true },
                              { label: 'Set Inventory Quantity', value: 'set_inventory' }
                            ]}
                            value={editingTemplate.prompt?.startsWith('tool:') ? editingTemplate.prompt.split(':')[1].split('|')[0] : ''}
                            onChange={(value) => {
                              // Set default parameters for the selected tool
                              let toolParams = '';
                              
                              switch(value) {
                                case 'image_alt_text':
                                  toolParams = ' - product image';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['image_alt_text'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'vendor_name':
                                  toolParams = 'Your Brand Name';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['vendor'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'generate_skus':
                                  toolParams = 'prefix=|suffix=';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.sku'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'generate_handles':
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['handle'],
                                    prompt: `tool:${value}|`
                                  }));
                                  break;
                                case 'handle_prefix_suffix':
                                  toolParams = 'prefix=|suffix=';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['handle'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'update_prices':
                                  toolParams = 'amount=10|type=percentage|field=price';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.price'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'sales_price_factor':
                                  toolParams = '1.1';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.price'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'round_prices':
                                  toolParams = '0.99';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.price'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'compare_price_discount':
                                  toolParams = '20';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.compare_at_price'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                case 'clear_compare_prices':
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.compare_at_price'],
                                    prompt: `tool:${value}|`
                                  }));
                                  break;
                                case 'set_inventory':
                                  toolParams = 'quantity=10|ignoreZero=yes';
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: ['variants.inventory_quantity'],
                                    prompt: `tool:${value}|${toolParams}`
                                  }));
                                  break;
                                default:
                                  // If no tool is selected, clear the prompt
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputFields: [],
                                    prompt: ''
                                  }));
                              }
                            }}
                            helpText="Select which tool you want to execute with this template"
                          />
                          
                          {editingTemplate.prompt?.startsWith('tool:') && (
                            <>
                              {/* Dynamic form fields based on the selected tool */}
                              {editingTemplate.prompt.startsWith('tool:image_alt_text') && (
                                <>
                                  <Banner status="info">
                                    <p><strong>Generate Image Alt Text</strong> - Creates SEO-friendly alt text for product images by combining the product title with a suffix.</p>
                                    <p>Example: For a product titled "Blue T-Shirt", using the suffix " - product image" will create alt text: "Blue T-Shirt - product image"</p>
                                  </Banner>
                                  <TextField
                                    label="Image Alt Text Suffix"
                                    value={editingTemplate.prompt.split('|')[1] || ' - product image'}
                                    onChange={(value) => {
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:image_alt_text|${value}`
                                      }));
                                    }}
                                    helpText="This text will be added to the product title to create the alt text for images."
                                    placeholder="e.g., - product image"
                                    autoComplete="off"
                                  />
                                </>
                              )}

                              {editingTemplate.prompt.startsWith('tool:vendor_name') && (
                                <TextField
                                  label="Vendor Name"
                                  value={editingTemplate.prompt.split('|')[1] || ''}
                                  onChange={(value) => {
                                    setEditingTemplate(prev => ({
                                      ...prev,
                                      prompt: `tool:vendor_name|${value}`
                                    }));
                                  }}
                                  helpText="This vendor name will be set for all selected products."
                                  placeholder="e.g., Your Brand Name"
                                  autoComplete="off"
                                />
                              )}

                              {editingTemplate.prompt.startsWith('tool:generate_skus') && (
                                <>
                                  <Banner status="info">
                                    <p>Generate SKUs for all variants of selected products using the format:</p>
                                    <p><code>[prefix]-[slugified product title]-[variant options]-[suffix]</code></p>
                                    <p>Each segment is optional. Variant options are included in order (e.g., Color/Size → black-xl).</p>
                                  </Banner>
                                  <FormLayout.Group>
                                    <TextField
                                      label="Prefix"
                                      value={skuPrefix}
                                      onChange={(value) => {
                                        setSkuPrefix(value);
                                        setEditingTemplate(prev => ({
                                          ...prev,
                                          prompt: `tool:generate_skus|prefix=${value}|suffix=${skuSuffix}`
                                        }));
                                      }}
                                      placeholder="e.g., WF"
                                      helpText="Added at the beginning of the SKU"
                                      autoComplete="off"
                                    />
                                    <TextField
                                      label="Suffix"
                                      value={skuSuffix}
                                      onChange={(value) => {
                                        setTimeout(() => {
                                          setSkuSuffix(value);
                                          const newPrompt = `tool:generate_skus|prefix=${skuPrefix}|suffix=${value}`;
                                          console.log('Setting new prompt with suffix:', newPrompt);
                                          setEditingTemplate(prev => ({
                                            ...prev,
                                            prompt: newPrompt
                                          }));
                                        }, 0);
                                      }}
                                      placeholder="e.g., 2025"
                                      helpText="Added at the end of the SKU"
                                      autoComplete="off"
                                    />
                                  </FormLayout.Group>
                                </>
                              )}

                              {editingTemplate.prompt.startsWith('tool:handle_prefix_suffix') && (
                                <FormLayout.Group>
                                  <TextField
                                    label="Prefix"
                                    value={handlePrefix}
                                    onChange={(value) => {
                                      setTimeout(() => {
                                        setHandlePrefix(value);
                                        setEditingTemplate(prev => ({
                                          ...prev,
                                          prompt: `tool:handle_prefix_suffix|prefix=${value}|suffix=${handleSuffix}`
                                        }));
                                      }, 0);
                                    }}
                                    onFocus={() => {
                                      const input = document.querySelector('input[aria-label="Prefix"]');
                                      if (input) {
                                        input.setAttribute('data-focused', 'true');
                                      }
                                    }}
                                    placeholder="e.g., new-"
                                    helpText="Added at the beginning of the handle"
                                    autoComplete="off"
                                  />
                                  <TextField
                                    label="Suffix"
                                    value={handleSuffix}
                                    onChange={(value) => {
                                      setTimeout(() => {
                                        setHandleSuffix(value);
                                        setEditingTemplate(prev => ({
                                          ...prev,
                                          prompt: `tool:handle_prefix_suffix|prefix=${handlePrefix}|suffix=${value}`
                                        }));
                                      }, 0);
                                    }}
                                    placeholder="e.g., -2023"
                                    helpText="Added at the end of the handle"
                                    autoComplete="off"
                                  />
                                </FormLayout.Group>
                              )}

                              {editingTemplate.prompt.startsWith('tool:update_prices') && (
                                <FormLayout.Group>
                                  <TextField
                                    label="Amount"
                                    type="number"
                                    value={(editingTemplate.prompt.match(/amount=([^|]*)/)?.[1] || '10')}
                                    onChange={(value) => {
                                      const type = editingTemplate.prompt.match(/type=([^|]*)/)?.[1] || 'percentage';
                                      const field = editingTemplate.prompt.match(/field=([^|]*)/)?.[1] || 'price';
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:update_prices|amount=${value}|type=${type}|field=${field}`
                                      }));
                                    }}
                                    helpText="Amount to adjust price by"
                                    autoComplete="off"
                                  />
                                  <Select
                                    label="Type"
                                    options={[
                                      {label: 'Percentage', value: 'percentage'},
                                      {label: 'Fixed Amount', value: 'fixed'}
                                    ]}
                                    value={(editingTemplate.prompt.match(/type=([^|]*)/)?.[1] || 'percentage')}
                                    onChange={(value) => {
                                      const amount = editingTemplate.prompt.match(/amount=([^|]*)/)?.[1] || '10';
                                      const field = editingTemplate.prompt.match(/field=([^|]*)/)?.[1] || 'price';
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:update_prices|amount=${amount}|type=${value}|field=${field}`
                                      }));
                                    }}
                                    helpText="Percentage or fixed amount"
                                  />
                                  <Select
                                    label="Field"
                                    options={[
                                      {label: 'Price', value: 'price'},
                                      {label: 'Compare at Price', value: 'compare_at_price'}
                                    ]}
                                    value={(editingTemplate.prompt.match(/field=([^|]*)/)?.[1] || 'price')}
                                    onChange={(value) => {
                                      const amount = editingTemplate.prompt.match(/amount=([^|]*)/)?.[1] || '10';
                                      const type = editingTemplate.prompt.match(/type=([^|]*)/)?.[1] || 'percentage';
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:update_prices|amount=${amount}|type=${type}|field=${value}`
                                      }));
                                    }}
                                    helpText="Which price field to update"
                                  />
                                </FormLayout.Group>
                              )}

                              {editingTemplate.prompt.startsWith('tool:sales_price_factor') && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '8px' }}>
                                    <Text variant="headingMd">Sales Price Factor</Text>
                                    <Tooltip content="Multiply current price by a factor (e.g., 1.1 for 10% increase)">
                                      <Icon source={QuestionMarkMinor} color="base" />
                                    </Tooltip>
                                  </div>
                                  <TextField
                                    label="Price Factor"
                                    type="number"
                                    step="0.01"
                                    value={editingTemplate.prompt.split('|')[1] || '1.1'}
                                    onChange={(value) => {
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:sales_price_factor|${value}`
                                      }));
                                    }}
                                    helpText="Multiply prices by this factor (e.g., 1.1 for 10% increase)"
                                    autoComplete="off"
                                  />
                                </>
                              )}

                              {editingTemplate.prompt.startsWith('tool:round_prices') && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '8px' }}>
                                    <Text variant="headingMd">Round Prices</Text>
                                    <Tooltip content="Round down to nearest dollar and add cents (e.g., 0.99 for $XX.99 pricing)">
                                      <Icon source={QuestionMarkMinor} color="base" />
                                    </Tooltip>
                                  </div>
                                  <TextField
                                    label="Round to Cents Value"
                                    type="number"
                                    step="0.01"
                                    value={editingTemplate.prompt.split('|')[1] || '0.99'}
                                    onChange={(value) => {
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:round_prices|${value}`
                                      }));
                                    }}
                                    helpText="Round down to nearest dollar and add cents (e.g., 0.99 for $XX.99 pricing)"
                                    autoComplete="off"
                                  />
                                </>
                              )}

                              {editingTemplate.prompt.startsWith('tool:compare_price_discount') && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '8px' }}>
                                    <Text variant="headingMd">Compare at Price Discount</Text>
                                    <Tooltip content="Set the discount percentage (0-100%). At 100%, the compare-at price will be removed.">
                                      <Icon source={QuestionMarkMinor} color="base" />
                                    </Tooltip>
                                  </div>
                                  <TextField
                                    label="Discount Percentage"
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={editingTemplate.prompt.split('|')[1] || '20'}
                                    onChange={(value) => {
                                      setEditingTemplate(prev => ({
                                        ...prev,
                                        prompt: `tool:compare_price_discount|${value}`
                                      }));
                                    }}
                                    helpText="Set the discount percentage (0-100%). At 100%, the compare-at price will be removed."
                                    suffix="%"
                                    autoComplete="off"
                                  />
                                </>
                              )}

                              {editingTemplate.prompt.startsWith('tool:set_inventory') && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '8px' }}>
                                    <Text variant="headingMd">Set Inventory Quantity</Text>
                                    <Tooltip content="Set inventory quantity for all selected products">
                                      <Icon source={QuestionMarkMinor} color="base" />
                                    </Tooltip>
                                  </div>
                                  <Text variant="bodyMd" color="subdued" as="p" style={{ marginBottom: '12px' }}>
                                    Inventory tracking needs to be activated in Shopify to let this tool work
                                  </Text>
                                  <FormLayout.Group>
                                    <TextField
                                      label="Quantity"
                                      type="number"
                                      value={(editingTemplate.prompt.split('|')[1] || '').match(/quantity=([^|]*)/)?.[1] || '10'}
                                      onChange={(value) => {
                                        const currentParams = editingTemplate.prompt.split('|')[1] || '';
                                        const ignoreZero = currentParams.match(/ignoreZero=([^|]*)/)?.[1] || 'yes';
                                        setEditingTemplate(prev => ({
                                          ...prev,
                                          prompt: `tool:set_inventory|quantity=${value}|ignoreZero=${ignoreZero}`
                                        }));
                                      }}
                                      helpText="Set a specific inventory quantity for all selected products"
                                      autoComplete="off"
                                    />
                                    <Stack vertical>
                                      <Text variant="bodyMd" as="p" fontWeight="medium">Ignore products with 0 inventory</Text>
                                      <Stack>
                                        <RadioButton
                                          label="Yes - Skip products with 0 inventory"
                                          id="ignore-zero-yes"
                                          name="ignoreZero"
                                          checked={!editingTemplate.prompt.includes('ignoreZero=no')}
                                          onChange={() => {
                                            // Get the current quantity value
                                            const currentPrompt = editingTemplate.prompt || '';
                                            const quantity = currentPrompt.includes('quantity=') ? 
                                              currentPrompt.split('quantity=')[1].split('|')[0] : '10';
                                            
                                            setEditingTemplate(prev => ({
                                              ...prev,
                                              prompt: `tool:set_inventory|quantity=${quantity}|ignoreZero=yes`
                                            }));
                                            console.log('Setting to YES');
                                          }}
                                        />
                                      </Stack>
                                      <Stack>
                                        <RadioButton
                                          label="No - Update all products"
                                          id="ignore-zero-no"
                                          name="ignoreZero"
                                          checked={editingTemplate.prompt.includes('ignoreZero=no')}
                                          onChange={() => {
                                            // Get the current quantity value
                                            const currentPrompt = editingTemplate.prompt || '';
                                            const quantity = currentPrompt.includes('quantity=') ? 
                                              currentPrompt.split('quantity=')[1].split('|')[0] : '10';
                                            
                                            setEditingTemplate(prev => ({
                                              ...prev,
                                              prompt: `tool:set_inventory|quantity=${quantity}|ignoreZero=no`
                                            }));
                                            console.log('Setting to NO');
                                          }}
                                        />
                                      </Stack>
                                    </Stack>
                                  </FormLayout.Group>
                                </>
                              )}

                              {/* For tools that don't need parameters */}
                              {(editingTemplate.prompt.startsWith('tool:generate_handles') || 
                                editingTemplate.prompt.startsWith('tool:clear_compare_prices')) && (
                                <Banner status="info">
                                  <p>This tool doesn't require any additional parameters.</p>
                                </Banner>
                              )}
                            </>
                          )}
                        </FormLayout>
                      </Card>
                    )}


                    {/* SEO Fields - Only show for regular product templates */}
                    {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>SEO Fields</Subheading>
                        </TextContainer>
                        <FormLayout>
                          <Checkbox
                            label="Product Image Alt Text"
                            checked={editingTemplate.outputFields?.includes('alt_text')}
                            onChange={(checked) => handleFieldChange('alt_text', checked, 'output')}
                          />
                        </FormLayout>
                      </Card>
                    )}

                    {/* Options Fields - Only show for regular product templates */}
                    {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Options</Subheading>
                        </TextContainer>
                        <FormLayout>
                          <Checkbox
                            label="Option1 Name"
                            checked={editingTemplate.outputFields?.includes('option1_name')}
                            onChange={(checked) => handleFieldChange('option1_name', checked, 'output')}
                          />
                          <Checkbox
                            label="Option1 Values"
                            checked={editingTemplate.outputFields?.includes('option1_values')}
                            onChange={(checked) => handleFieldChange('option1_values', checked, 'output')}
                          />
                          <Checkbox
                            label="Option2 Name"
                            checked={editingTemplate.outputFields?.includes('option2_name')}
                            onChange={(checked) => handleFieldChange('option2_name', checked, 'output')}
                          />
                          <Checkbox
                            label="Option2 Values"
                            checked={editingTemplate.outputFields?.includes('option2_values')}
                            onChange={(checked) => handleFieldChange('option2_values', checked, 'output')}
                          />
                          <Checkbox
                            label="Option3 Name"
                            checked={editingTemplate.outputFields?.includes('option3_name')}
                            onChange={(checked) => handleFieldChange('option3_name', checked, 'output')}
                          />
                          <Checkbox
                            label="Option3 Values"
                            checked={editingTemplate.outputFields?.includes('option3_values')}
                            onChange={(checked) => handleFieldChange('option3_values', checked, 'output')}
                          />
                        </FormLayout>
                      </Card>
                    )}

                    {/* Variant Fields */}
                    {editingTemplate.type === 'variant' && (
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Variant Fields</Subheading>
                        </TextContainer>
                        <FormLayout>
                          <Checkbox
                            label="SKU"
                            checked={editingTemplate.outputFields?.includes('variants.sku')}
                            onChange={(checked) => handleFieldChange('variants.sku', checked, 'output')}
                          />
                          <Checkbox
                            label="Price"
                            checked={editingTemplate.outputFields?.includes('variants.price')}
                            onChange={(checked) => handleFieldChange('variants.price', checked, 'output')}
                          />
                          <Checkbox
                            label="Compare at Price"
                            checked={editingTemplate.outputFields?.includes('variants.compare_at_price')}
                            onChange={(checked) => handleFieldChange('variants.compare_at_price', checked, 'output')}
                          />
                          <Checkbox
                            label="Inventory Quantity"
                            checked={editingTemplate.outputFields?.includes('variants.inventory_quantity')}
                            onChange={(checked) => handleFieldChange('variants.inventory_quantity', checked, 'output')}
                          />
                        </FormLayout>
                      </Card>
                    )}
                    
                    {/* Output Metafields - Only show for regular product templates */}
                    {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                      <Card sectioned>
                        <TextContainer>
                          <Subheading>Output Metafields</Subheading>
                          <p>Select metafields that will be updated by the template</p>
                        </TextContainer>
                        <FormLayout>
                        {(editingTemplate.outputMetafields || []).map((metafield, index) => (
                          <FormLayout.Group key={index} condensed>
                            <TextField
                              label="Namespace"
                              value={metafield.namespace}
                              onChange={(value) => handleMetafieldChange(index, 'namespace', value, 'output')}
                              autoComplete="off"
                              helpText={index === 0 ? "Example: custom" : undefined}
                            />
                            <TextField
                              label="Key"
                              value={metafield.key}
                              onChange={(value) => handleMetafieldChange(index, 'key', value, 'output')}
                              autoComplete="off"
                              helpText={index === 0 ? "Example: description_extended" : undefined}
                            />
                            {/* Add output mode selector for metafields - Only show when not in Sidebar Only mode */}
                            {editingTemplate.type === 'image' && !editingTemplate.sidebarOnly && (
                              <Select
                                label="Insert Mode"
                                options={[
                                  { label: 'Exchange', value: 'exchange' },
                                  { label: 'Add', value: 'add' }
                                ]}
                                value={editingTemplate.outputMetafieldModes?.[`${metafield.namespace}.${metafield.key}`] === 'add' ? 'add' : 'exchange'}
                                onChange={(value) => {
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    outputMetafieldModes: {
                                      ...(prev.outputMetafieldModes || {}),
                                      [`${metafield.namespace}.${metafield.key}`]: value
                                    }
                                  }));
                                }}
                              />
                            )}
                            <Button
                              plain
                              destructive
                              onClick={() => handleRemoveMetafield(index, 'output')}
                            >
                              Remove
                            </Button>
                          </FormLayout.Group>
                        ))}
                        <Button onClick={() => handleAddMetafield('output')}>
                          Add Output Metafield
                        </Button>
                      </FormLayout>
                    </Card>
                    )}
                  </FormLayout>
                </Card>
              </FormLayout>
            </Card>
          </Stack>
        </Modal.Section>
      </Modal>

      {/* AI Model Help Modal */}
      <Modal
        open={showAIModelHelpModal}
        onClose={() => setShowAIModelHelpModal(false)}
        title="💡 Choose the Right AI Model for Your Task"
        primaryAction={{
          content: 'Got it',
          onAction: () => setShowAIModelHelpModal(false)
        }}
      >
        <Modal.Section>
          <TextContainer>
            <Heading>🧠 What's an AI Model?</Heading>
            <p>Different models specialize in different tasks. Some are fast and efficient, others are more powerful and better at complex writing or reasoning — or even analyzing product images.</p>
            <p>Choosing the right model helps you get better results — and saves you credits.</p>
            
            <Heading>⚙️ Available Models</Heading>
            
            <Stack vertical spacing="loose">
              {/* GPT-4o Mini */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>GPT-4o Mini</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Short content, specifications, translations, option values</TextStyle>
                  <p>Why use it: Fast, cheap, and good for simple structured tasks</p>
                  <TextStyle variation="strong">Credits per output: 1</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Great for bulk updates and small text tasks</p>
                  </div>
                </Stack>
              </Card>
              
              {/* GPT-4o */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>GPT-4o</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Product descriptions, titles, benefits, SEO-friendly content</TextStyle>
                  <p>Why use it: Balanced speed and quality — great general-purpose model</p>
                  <TextStyle variation="strong">Credits per output: 2</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Perfect for high-quality product listings</p>
                  </div>
                </Stack>
              </Card>
              
              {/* GPT-4.1 Mini */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>GPT-4.1 Mini</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Translations, options, basic titles/specs</TextStyle>
                  <p>Why use it: Slightly smarter than GPT-4o Mini, but still cost-efficient</p>
                  <TextStyle variation="strong">Credits per output: 1</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Use it for structured automation with minimal cost</p>
                  </div>
                </Stack>
              </Card>
              
              {/* GPT-4.1 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>GPT-4.1</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Premium descriptions, SEO, comparisons, benefits</TextStyle>
                  <p>Why use it: More context understanding and structured output quality</p>
                  <TextStyle variation="strong">Credits per output: 2</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Use for longer or brand-critical content</p>
                  </div>
                </Stack>
              </Card>
              
              {/* DeepSeek V3 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🔍</div>
                    <Heading>DeepSeek V3</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Specs, bullet points, short structured info</TextStyle>
                  <p>Why use it: Clean, factual writing — ideal for technical stores</p>
                  <TextStyle variation="strong">Credits per output: 1</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Great for dropshipping specs or Amazon-style features</p>
                  </div>
                </Stack>
              </Card>
              
              {/* DeepSeek R1 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#9C27B0', fontSize: '24px', marginRight: '8px' }}>🧠</div>
                    <Heading>DeepSeek R1</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Advanced reasoning, comparisons, benefit explanations</TextStyle>
                  <p>Why use it: High-quality logic and structure, handles complexity well</p>
                  <TextStyle variation="strong">Credits per output: 3</TextStyle>
                  <div style={{ color: '#9C27B0' }}>
                    <p>💡 Use for benefit texts, detailed variants, or comparison tables</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini Models Section */}
              <Heading>🌟 Gemini Models</Heading>
              
              {/* Gemini 1.5 Flash */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>Gemini 1.5 Flash</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Specs, options, fast translations, short text from images</TextStyle>
                  <p>Why use it: Ultra-fast and cost-efficient for simple tasks</p>
                  <TextStyle variation="strong">Credits per output: 2 (text or 1 image)</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Use when speed is key — works well with a single product image</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 2.0 Flash */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>Gemini 2.0 Flash</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Image-based titles, short benefits, comparison logic</TextStyle>
                  <p>Why use it: More image context support and improved reasoning</p>
                  <TextStyle variation="strong">Credits per output: 3 (supports up to 3 images)</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Perfect for products with multiple images — and still fast</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 2.5 Flash */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>Gemini 2.5 Flash</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Slightly deeper visual analysis, image-heavy product specs</TextStyle>
                  <p>Why use it: Multimodal and preview of next-gen speed + quality</p>
                  <TextStyle variation="strong">Credits per output: 3–4 (up to 3 images)</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Best balance between image support and cost-efficiency</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 1.5 Pro */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>Gemini 1.5 Pro</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: SEO-rich descriptions, detailed feature extraction from image</TextStyle>
                  <p>Why use it: Premium quality + strong image understanding</p>
                  <TextStyle variation="strong">Credits per output: 5–7 (up to 3 images)</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Use for complex listings and high-value content</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 2.5 Pro */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>Gemini 2.5 Pro (Preview)</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: AI-rich vision tasks, highly branded content from product photos</TextStyle>
                  <p>Why use it: Next-gen quality, advanced reasoning & visual logic</p>
                  <TextStyle variation="strong">Credits per output: 6–9 (up to 3 images)</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Recommended for feature analysis, multi-image evaluations</p>
                  </div>
                </Stack>
              </Card>
            </Stack>
            
            <Heading>📌 Tip: What's an "Output"?</Heading>
            <p>Each generated field (title, description, benefit, etc.) counts as one output.</p>
            <p>The model's credit cost applies per output field — including vision templates.</p>
            
            <Heading>📉 Want to save credits?</Heading>
            <p>Use cheaper models like GPT-4o Mini, DeepSeek V3 or Gemini Flash for:</p>
            <ul>
              <li>Generating options/specs</li>
              <li>Translating short values</li>
              <li>Product title variations</li>
              <li>Descriptions based on 1 product image</li>
            </ul>
            
            <Heading>🎯 Still not sure?</Heading>
            <p>You can select a different model for each template.</p>
            <p>Start with a fast model — upgrade to a smarter one if you need better output.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Image Template Executor Modal - Only for image selection */}
      {showImageTemplateExecutor && currentImageTemplate && !Boolean(currentImageTemplate.useFeaturedImage) && (
        <Modal
          open={showImageTemplateExecutor}
          onClose={() => setShowImageTemplateExecutor(false)}
          title={`Select Images for Template: ${currentImageTemplate.name}`}
          large
        >
          <Modal.Section>
            <ImageTemplateExecutor
              template={currentImageTemplate}
              products={selectedProducts}
              onUpdate={(productId, updates) => {
                // Handle product updates
                if (handleProductChange) {
                  console.log('Manual image selection - handling product update');
                  // Handle the update based on the field
                  if (updates.body_html) {
                    console.log('Manual image selection - handling product update with body_html content');
                    
                    // Get the existing product and its content
                    const product = selectedProducts.find(p => p.id === productId);
                    
                    // CRITICAL: Use the same thorough approach as auto-process mode to find existing content
                    const existingContent = {};
                    
                    // Try multiple ways to find the product description
                    if (product) {
                      console.log(`Manual image selection - checking existing content for product ${product.id}`);
                      
                      // Try all possible places where description might be stored
                      const possibleSources = [
                        product.body_html,
                        product.descriptionHtml,
                        product.description,
                        // Check if there's an HTML description in metafields
                        product.metafields?.find(m => m.key === 'description_html')?.value,
                        // Check for SEO description
                        product.metafields?.find(m => m.key === 'description' && m.namespace === 'seo')?.value
                      ];
                      
                      // Use the first non-empty source
                      const existingDescription = possibleSources.find(source => source && source.trim() !== '');
                      existingContent.body_html = existingDescription;
                      
                      console.log(`Manual image selection - found existing description with length: ${existingContent.body_html.length}`);
                      if (existingContent.body_html) {
                        console.log(`Manual image selection - existing description starts with: ${existingContent.body_html.substring(0, 50)}...`);
                      }
                    }
                    
                    // Create the updates object with the same structure as auto-process mode
                    const updatedFields = {
                      body_html: updates.body_html,
                      insertModes: {}
                    };
                    
                    // For body_html, we always want to use 'add' mode regardless of configuration
                    updatedFields.insertModes.body_html = 'add';
                    console.log(`Manual image selection - setting 'add' mode for body_html field in TemplateTools`);
                    
                    // Format the content with paragraph tags if needed
                    if (updatedFields.body_html && typeof updatedFields.body_html === 'string' && !updatedFields.body_html.includes('<p>')) {
                      updatedFields.body_html = `<p>${updatedFields.body_html}</p>`;
                      console.log(`Manual image selection - formatted body_html content with paragraph tags`);
                    } else if (updatedFields.body_html && typeof updatedFields.body_html !== 'string') {
                      console.log(`Warning: body_html content is not a string: ${typeof updatedFields.body_html}`);
                      // Convert to string properly if it's an object
                      if (typeof updatedFields.body_html === 'object' && updatedFields.body_html !== null) {
                        console.log('Converting object to formatted string for body_html');
                        // Format the object for readability
                        if (updatedFields.body_html.body_html) {
                          // If the object has a body_html property, use that directly
                          updatedFields.body_html = updatedFields.body_html.body_html;
                        } else {
                          // Otherwise, format the entire object for readability
                          updatedFields.body_html = JSON.stringify(updatedFields.body_html, null, 2);
                        }
                      } else {
                        // For other types, use String conversion
                        updatedFields.body_html = String(updatedFields.body_html);
                      }
                      
                      // Add paragraph tags if needed
                      if (!updatedFields.body_html.includes('<p>')) {
                        updatedFields.body_html = `<p>${updatedFields.body_html}</p>`;
                      }
                    }
                    
                    // CRITICAL: Pass the existing content for ALL fields that use 'add' mode
                    if (updatedFields.insertModes.body_html === 'add') {
                      // Initialize _originalContent if it doesn't exist
                      if (!updatedFields._originalContent) {
                        updatedFields._originalContent = {};
                      }
                      
                      // For standard fields, use the existingContent object
                      if (existingContent && existingContent.body_html) {
                        updatedFields._originalContent.body_html = existingContent.body_html;
                        console.log(`Manual image selection - passing existing content directly for body_html: ${existingContent.body_html.substring(0, 50)}...`);
                      }
                    }
                    
                    // Log what we're updating
                    console.log(`Manual image selection - updating product ${productId} with:`, {
                      fields: Object.keys(updatedFields).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
                      insertModes: updatedFields.insertModes,
                      hasOriginalContent: !!updatedFields._originalContent
                    });
                    
                    // Call handleProductChange exactly like in auto-process mode
                    handleProductChange(productId, 'all', updatedFields);
                  } else {
                    // For other fields, use the standard approach
                    handleProductChange(productId, 'all', updates);
                  }
                }
                
                // Record template usage
                recordTemplateUsage(user?.uid, currentShop?.id, currentImageTemplate.id);
              }}
              onProgress={(progressPercent) => {
                // Update progress in the parent component - cap at 100%
                const cappedProgress = Math.min(progressPercent, 100);
                setProgressSafe(cappedProgress);
                
                // Show processing status with progress
                setIsExecuting(true);
                setWorkflowExecutionStatus({
                  status: 'processing',
                  message: `Processing image template '${currentImageTemplate.name}'... ${Math.round(cappedProgress)}%`
                });
                
                // If progress is 100%, we're done processing the image template
                if (cappedProgress >= 100) {
                  console.log('Manual image selection reached 100% - completing image template execution');
                  
                  // Check if there are remaining templates to process
                  if (remainingAfterImage && remainingAfterImage.length > 0) {
                    console.log('Manual image selection - processing remaining templates:', remainingAfterImage);
                    
                    // Set status to indicate we're moving to the next templates
                    setWorkflowExecutionStatus({
                      status: 'processing',
                      message: `Image template '${currentImageTemplate.name}' completed. Processing remaining templates...`
                    });
                    
                    // Close the image template executor modal
                    setShowImageTemplateExecutor(false);
                    
                    // Process the remaining templates after a short delay
                    setTimeout(async () => {
                      try {
                        // Execute the remaining templates
                        console.log('Executing remaining templates after image template:', remainingAfterImage);
                        
                        const remainingResult = await executeTemplatesDirectly(
                          remainingAfterImage,
                          selectedProducts, // Pass the full product objects
                          (status) => {
                            // Scale the progress from 50-100% for the remaining templates
                            const scaledProgress = status.progress ? 50 + (status.progress / 2) : 50;
                            setProgressSafe(scaledProgress);
                            
                            // Update the status message
                            setWorkflowExecutionStatus(status);
                            
                            // If workflow is complete, reset the executing state
                            if (status.status === 'completed') {
                              setTimeout(() => {
                                setIsExecuting(false);
                                setProgressSafe(0);
                                setWorkflowExecutionStatus(null);
                              }, 1500);
                            }
                          }
                        );
                        
                        console.log('Remaining templates processed:', remainingResult);
                      } catch (error) {
                        console.error('Error processing remaining templates:', error);
                        setWorkflowExecutionStatus({
                          status: 'error',
                          message: `Error processing remaining templates: ${error.message}`
                        });
                        
                        // Reset execution state on error
                        setTimeout(() => {
                          setIsExecuting(false);
                          setProgressSafe(0);
                        }, 1500);
                      }
                    }, 500);
                  } else {
                    // No remaining templates, just complete the workflow
                    console.log('Manual image selection - no remaining templates, completing workflow');
                    
                    // Set success status
                    setWorkflowExecutionStatus({
                      status: 'success',
                      message: `Image template '${currentImageTemplate.name}' processed successfully.`
                    });
                    
                    // Reset execution state after a short delay
                    setTimeout(() => {
                      console.log('Manual image selection - execution state reset after completion');
                      setIsExecuting(false);
                      setProgressSafe(0);
                      setShowImageTemplateExecutor(false);
                    }, 1500);
                  }
                }
              }}
              onSaveResults={(results) => {
                console.log('Saving results to persistent sidebar:', results);
                // Add results to the persistent sidebar
                setPersistentResults(prev => {
                  // Create a map of existing results by ID for quick lookup
                  const existingResultsMap = {};
                  prev.forEach(result => {
                    if (result.id) {
                      existingResultsMap[result.id] = true;
                    }
                  });
                  
                  // Filter out duplicates from new results
                  const uniqueNewResults = results.filter(result => {
                    return !result.id || !existingResultsMap[result.id];
                  });
                  
                  console.log(`Adding ${uniqueNewResults.length} unique results to existing ${prev.length} results`);
                  return [...prev, ...uniqueNewResults];
                });
                
                // Show the sidebar
                setSidebarVisible(true);
                
                // CRITICAL: Immediately set execution state to false and progress to 100%
                // This ensures the loading indicator completes properly
                console.log('Manual image selection complete - immediately resetting execution state');
                setIsExecuting(false);
                setProgressSafe(100); // Ensure progress is complete
                setWorkflowExecutionStatus({
                  status: 'success',
                  message: `Image template '${currentImageTemplate.name}' processed successfully.`
                });
                
                // Close the modal to ensure UI is updated
                setShowImageTemplateExecutor(false);
                
                // Clear the status after a short delay to prevent it from staying stuck
                setTimeout(() => {
                  setWorkflowExecutionStatus(null);
                  setProgressSafe(0);
                  console.log('Progress and workflow status reset after manual image selection');
                }, 1500); // Shorter timeout for better UX
              }}
              onClose={() => setShowImageTemplateExecutor(false)}
            />
          </Modal.Section>
        </Modal>
      )}
      {/* Persistent Results Sidebar - always rendered at the top level */}
      <PersistentResultsSidebar
        results={persistentResults}
        visible={sidebarVisible}
        onToggleVisibility={(visible) => setSidebarVisible(visible)}
        onSave={(editedResults) => {
          console.log('Saving edited results from sidebar:', editedResults);
          
          // Update products with edited content
          if (handleProductChange) {
            Object.entries(editedResults).forEach(([resultId, fields]) => {
              handleProductChange(resultId, 'all', fields);
            });
          }
        }}
      />
    </div>
  );
}
